<template>
  <v-container fluid class="pa-2 pa-md-3 mb-6">
    <v-alert
      v-if="!isSingleQuestion && progressReadOnly"
      type="info"
      variant="tonal"
      class="mb-4"
    >
      Progress was reset. You can review this session, or create a new one to
      practise.
    </v-alert>
    <v-progress-linear v-if="answerSaving" indeterminate aria-label="Saving your answer" class="mb-3" />
    <div id="confettiDiv"></div>
    <v-navigation-drawer
      v-model="questionDrawer"
      :width="questionDrawerSize"
      temporary
      location="end"
      order="2"
      class="px-12 py-6"
    >
      <v-row wrap class="pr-3 pt-6">
        <v-col
          cols="4"
          lg="3"
          v-for="(question, idx) in sessionData?.questions"
        >
          <v-hover v-slot="{ isHovering, props }">
            <div
              v-bind="props"
              v-if="questionDrawer"
              @click="
                questionIndex = idx;
                questionDrawer = false;
                showOverview = false;
              "
              style="
                cursor: pointer;
                height: 50px;
                width: 50px;
                border: #03045e 1px solid;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative; /* Added to position the v-badge */
              "
              class="rounded-lg text-center"
              :class="{
                'elevation-3': isHovering,
                'bg-progress':
                  question.chosenAnswerId !== null &&
                  sessionData?.enableTestMode &&
                  !showResults,
                'bg-green':
                  question.correct &&
                  (!sessionData?.enableTestMode || showResults),
                'bg-red':
                  !question.correct &&
                  (!sessionData?.enableTestMode || showResults),
                'bg-bw': question.chosenAnswerId === null,
                'thick-outline': idx === questionIndex,
              }"
              v-motion
              :initial="{ opacity: 0, y: -15 }"
              :enter="{ opacity: 1, y: 0, scale: 1 }"
              :delay="curvedDelay(idx)"
            >
              <strong>{{ idx + 1 }}</strong>
              <v-badge
                v-if="
                  sessionFlaggedQuestionIds.includes(question.questionId)
                "
                color="error"
                inline
                style="
                  position: absolute; /* Position the badge */
                  top: -10px; /* Align to the top */
                  right: -10px; /* Align to the right */
                "
              >
                <!-- Content slot -->
                <template v-slot:badge>
                  <v-icon small>mdi-flag</v-icon>
                </template>
              </v-badge>
            </div>
          </v-hover>
        </v-col>
      </v-row>
    </v-navigation-drawer>
    <v-row wrap class="mb-12">
      <v-col cols="12">
        <v-card v-if="showOverview" class="pa-2 pa-md-8">
          <v-row>
            <v-col cols="6" class="d-flex justify-start">
              <v-chip
                @click="
                  showOverview = false;
                  questionIndex = 0;
                "
                class="bg-primary"
                label
                prepend-icon="mdi-arrow-left"
                v-motion
                :initial="{ x: -10 }"
                :enter="{ x: 0, scale: 1 }"
                :delay="0"
                :size="mobile ? 'small' : 'default'"
              >
                Back To Questions
              </v-chip>
            </v-col>

            <v-col cols="4" offset="2" class="d-flex justify-end">
              <v-chip
                @click="questionDrawer = true"
                class="bg-primary"
                label
                prepend-icon="mdi-format-list-bulleted"
                v-motion
                :initial="{ x: 10 }"
                :enter="{ x: 0, scale: 1 }"
                :delay="0"
                :size="mobile ? 'small' : 'default'"
              >
                Overview
              </v-chip>
            </v-col>
            <v-col cols="12" class="d-flex justify-start">
              <v-chip
                @click="router.push('/')"
                class="bg-primary"
                label
                prepend-icon="mdi-home"
                v-motion
                :initial="{ x: -10 }"
                :enter="{ x: 0, scale: 1 }"
                :delay="100"
                :size="mobile ? 'small' : 'default'"
              >
                To Dashboard
              </v-chip>
            </v-col>
            <v-col
              cols="12"
              class="text-center text-h4"
              v-motion
              :initial="{ y: 50, opacity: 0 }"
              :enter="{ y: 0, opacity: 1 }"
              :delay="100"
            >
              Results
            </v-col>
            <v-col cols="12" class="text-center text-h5 mt-6">
              <v-progress-circular
                :size="200"
                :width="30"
                color="green"
                bg-color="red"
                :model-value="
                  perc(
                    sessionData?.finalStats?.correct,
                    sessionData?.numberOfQuestions,
                  )
                "
                v-motion
                :initial="{ y: 50, opacity: 0 }"
                :enter="{ y: 0, opacity: 1 }"
                :delay="200"
              >
                <strong>
                  {{
                    perc(
                      sessionData?.finalStats?.correct,
                      sessionData?.numberOfQuestions,
                    )
                  }}%</strong
                >
              </v-progress-circular>
            </v-col>
            <v-col
              cols="12"
              md="4"
              offset-md="4"
              class="text-center mt-6"
              v-motion
              :initial="{ y: 50, opacity: 0 }"
              :enter="{ y: 0, opacity: 1 }"
              :delay="250"
            >
              <div>
                <strong>Overall Breakdown</strong>
              </div>
              <v-table class="text-caption mt-3" density="compact">
                <tbody>
                  <tr>
                    <td>Correct</td>
                    <td>
                      {{ sessionData?.finalStats?.correct }}/{{
                        sessionData?.numberOfQuestions
                      }}
                    </td>
                    <td>
                      ({{
                        perc(
                          sessionData?.finalStats?.correct,
                          sessionData?.numberOfQuestions,
                        )
                      }}%)
                    </td>
                  </tr>
                  <tr>
                    <td>Incorrect</td>
                    <td>
                      {{ sessionData?.finalStats?.incorrect }}/{{
                        sessionData?.numberOfQuestions
                      }}
                    </td>
                    <td>
                      ({{
                        perc(
                          sessionData?.finalStats?.incorrect,
                          sessionData?.numberOfQuestions,
                        )
                      }}%)
                    </td>
                  </tr>
                  <tr>
                    <td>Unanswered</td>
                    <td>
                      {{ sessionData?.finalStats?.unanswered }}/{{
                        sessionData?.numberOfQuestions
                      }}
                    </td>
                    <td>
                      ({{
                        perc(
                          sessionData?.finalStats?.unanswered,
                          sessionData?.numberOfQuestions,
                        )
                      }}%)
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col
              cols="12"
              md="6"
              offset-md="3"
              class="text-center mt-6"
              v-motion
              :initial="{ y: 50, opacity: 0 }"
              :enter="{ y: 0, opacity: 1 }"
              :delay="300"
            >
              <div>
                <strong>Breakdown By Category</strong>
              </div>
              <v-table class="text-caption mt-3" density="compact">
                <thead>
                  <tr>
                    <th class="text-center">Category</th>
                    <th class="text-center">Correct</th>
                    <th class="text-center">Incorrect</th>
                    <th class="text-center">Unanswered</th>
                  </tr>
                </thead>
                <tbody v-if="sessionData?.finalStats?.categoryStats">
                  <tr
                    v-for="(cat, idx) in Object.keys(
                      sessionData?.finalStats?.categoryStats,
                    )"
                    :key="idx"
                  >
                    <td>{{ cat }}</td>
                    <td>
                      {{ sessionData?.finalStats?.categoryStats[cat].correct }}
                    </td>
                    <td>
                      {{
                        sessionData?.finalStats?.categoryStats[cat].incorrect
                      }}
                    </td>
                    <td>
                      {{
                        sessionData?.finalStats?.categoryStats[cat].unanswered
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
              <div class="mt-12 text-center text-caption text-disabled">
                <i>
                  Note: Unanswered questions are counted as attempted and
                  incorrect.
                </i>
              </div>
            </v-col>
          </v-row>
        </v-card>
        <v-card v-else class="pa-2 pa-md-8" min-height="95vh">
          <v-row v-if="loading">
            <v-col cols="12" class="text-center">
              <v-progress-circular
                indeterminate
                color="progress"
              ></v-progress-circular>
              <div class="text-disabled text-caption mt-6">
                Loading
                {{
                  Math.round(
                    (numberOfQuestionsLoading / numberOfQuestionsTotal) * 100,
                  ) || 0
                }}%
              </div>
            </v-col>
          </v-row>

          <v-row wrap v-else>
            <v-col cols="12">
              <v-progress-linear
                :model-value="
                  ((questionIndex + 1) / sessionData?.numberOfQuestions) * 100
                "
                color="progress"
                rounded
                height="10"
              ></v-progress-linear>
            </v-col>

            <v-col
              cols="6"
              :md="
                sessionData.enableTimer &&
                sessionData.status !== 'completed' &&
                !progressReadOnly
                  ? '4'
                  : '6'
              "
            >
              <div v-if="isAdmin && isSingleQuestion" class="mb-4">
                <v-btn
                  @click="adminEditQuestion"
                  :loading="editQuestionButtonLoading"
                  color="primary"
                >
                  Edit Question
                </v-btn>
              </div>
              <div v-if="forceDraft && isAdmin" cols="12">
                <AdminDraftPreviewApprovalActions
                  class="mb-6"
                  compact
                  :pending="isApprovalPending"
                  :can-approve-close="canApproveClose"
                  :can-approve-next="canApproveNext"
                  :completion-recovery="completionRecovery"
                  :approval-ready="draftApprovalReady"
                  :issues="draftApprovalIssues"
                  @approve="handleDraftPreviewApproval"
                  @recover="recoverFromCompletionError"
                />
              </div>
              <!-- <div
                v-if="
                  ['Super Admin', 'Admin'].includes(currentUserLevel) &&
                  isSingleQuestion &&
                  forceDraft
                "
                class="mb-4"
              >
                <v-btn
                  color="error"
                  :loading="isDeleting"
                  @click="handleDelete"
                  prepend-icon="mdi-delete"
                  class="mr-2"
                >
                  Delete Draft
                </v-btn>

                <v-btn
                  color="success"
                  :loading="isApproving"
                  @click="handleApprove"
                  prepend-icon="mdi-check-circle"
                  :disabled="!isQuestionComplete"
                >
                  Approve
                </v-btn>
              </div> -->
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <div class="font-weight-bold">
                  <template v-if="!isSingleQuestion">
                    Question {{ questionIndex + 1 }} of
                    {{ sessionData.numberOfQuestions }}
                  </template>
                  <template v-else> Question {{ route.params.id }} </template>
                </div>
                <v-tooltip text="Click to copy a direct link to this question">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      icon="mdi-content-copy"
                      variant="text"
                      size="small"
                      color="primary"
                      :loading="copyQuestionLinkLoading"
                      @click="copyQuestionLink"
                    />
                  </template>
                </v-tooltip>
              </div>
              <div
                v-if="
                  !isSingleQuestion &&
                  (sessionData.status === 'completed' ||
                    !sessionData?.enableTestMode)
                "
                style="font-size: smaller"
                class="mt-1"
              >
                Correct: {{ progressSoFar.num }}/{{
                  progressSoFar.totalAnswered
                }}
                ({{ progressSoFar.perc }}%)
              </div>
              <div class="d-flex align-center mt-2" style="gap: 8px">
                <v-chip size="small">
                  {{ currentQuestionData?.category?.id || "Uncategorised" }}
                </v-chip>
              </div>
            </v-col>
            <v-col
              v-if="
                sessionData.enableTimer &&
                sessionData.status !== 'completed' &&
                !progressReadOnly
              "
              class="hidden-sm-and-down"
              cols="4"
            >
              <div class="text-center">
                <TestCountDown :secondsRemaining="secondsRemaining" />
              </div>
            </v-col>
            <v-col
              cols="6"
              :md="
                sessionData.enableTimer &&
                sessionData.status !== 'completed' &&
                !progressReadOnly
                  ? '4'
                  : '6'
              "
              class="d-flex justify-end"
            >
              <v-chip
                v-if="!isSingleQuestion"
                @click="questionDrawer = true"
                class="bg-primary"
                label
                prepend-icon="mdi-format-list-bulleted"
                v-motion
                :initial="{ opacity: 0, x: 10 }"
                :enter="{ opacity: 1, x: 0, scale: 1 }"
                :delay="200"
                :size="mobile ? 'small' : 'default'"
              >
                Overview
              </v-chip>
            </v-col>
            <v-col
              v-if="
                sessionData.enableTimer &&
                sessionData.status !== 'completed' &&
                !progressReadOnly
              "
              class="hidden-md-and-up"
              cols="12"
            >
              <div class="text-center">
                <TestCountDown :secondsRemaining="secondsRemaining" />
              </div>
            </v-col>

            <v-col
              v-if="currentQuestionData?.retired && !forceDraft"
              cols="12"
            >
              <v-alert
                type="info"
                variant="tonal"
                border="start"
                class="mb-0"
              >
                <div class="font-weight-bold mb-1">Question withdrawn</div>
                <div>
                  This question has been withdrawn from the active question
                  bank and will not appear in new sessions. It remains visible
                  because it was already included in this session.
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" v-if="forceDraft">
              <v-alert
                type="warning"
                variant="tonal"
                border="start"
                class="mb-0"
                density="compact"
              >
                <div class="d-flex align-center">
                  <span>Draft Question</span>
                </div>
              </v-alert>
            </v-col>

            <v-col cols="12" v-else-if="isAdmin && draftExists && !forceDraft">
              <v-alert type="info" variant="tonal" border="start" class="mb-4">
                <div class="d-flex align-center justify-space-between">
                  <span>A draft version of this question exists.</span>
                  <v-btn
                    color="primary"
                    variant="text"
                    :to="{ query: { forceDraft: true } }"
                    target="_blank"
                  >
                    View Draft
                  </v-btn>
                </div>
              </v-alert>
            </v-col>
            <!-- hide these chips when in draft mode -->
            <v-col v-if="showElements && !forceDraft" cols="12">
              <QuestionInfoChips
                :qId="sessionData?.questions[questionIndex].questionId"
                :currentQuestionData="currentQuestionData"
                :userQuestions="sessionUserQuestions"
                :showStatChips="showResults"
                :isSingleQuestion="isSingleQuestion"
                :collectionPath="collectionPath"
              />
            </v-col>
            <v-col v-if="showElements" cols="12">
              <div v-if="showUnansweredChip">
                <v-chip
                  class="bg-progress mt-2"
                  label
                  prepend-icon="mdi-alert-circle-outline"
                  v-motion
                  :initial="{ opacity: 0, y: -10 }"
                  :enter="{ opacity: 1, y: 0, scale: 1 }"
                  :delay="100"
                >
                  Unanswered
                </v-chip>
              </div>

              <QuestionPreview
                :question-data="currentQuestionPreviewData"
                :all-img-paths="allImgPaths"
                :show-results="showResults"
                :answers-disabled="answerSaving || progressReadOnly"
                :chosen-answer-id="
                  sessionData?.questions?.[questionIndex]?.chosenAnswerId
                "
                :explanation-last-updated-display="
                  explanationLastUpdatedDisplay
                "
                @answer="({ option, index }) => handleAnswer(option, index)"
              >
                <template #option-stats="{ option }">
                  <v-col
                    v-if="showResults"
                    class="hidden-sm-and-down"
                    cols="12"
                  >
                    <v-tooltip
                      content-class="bg-primary"
                      :text="`Selected ${perc(
                        currentQuestionData.stats.answerSelections[option.oID],
                        currentQuestionData.stats.attempted,
                      )}% of the time by others.`"
                      location="bottom"
                    >
                      <template v-slot:activator="{ props }">
                        <v-progress-linear
                          v-bind="props"
                          :model-value="
                            perc(
                              currentQuestionData.stats.answerSelections[
                                option.oID
                              ],
                              currentQuestionData.stats.attempted,
                            )
                          "
                          color="progress"
                          rounded
                          height="5"
                        ></v-progress-linear>
                      </template>
                    </v-tooltip>
                  </v-col>
                </template>

                <template #question-rating>
                  <div
                    v-if="shouldShowQuestionRating"
                    v-motion
                    :initial="{ opacity: 0, y: -10 }"
                    :enter="{ opacity: 1, y: 0, scale: 1 }"
                    :delay="100"
                    class="mt-4"
                  >
                    <ContentRatingPrompt
                      label="Rate this question"
                      :feedback-options="questionRatingFeedbackOptions"
                      :loading="questionRatingSubmitting"
                      @rate="handleQuestionRating"
                      @dismiss="dismissQuestionRating"
                    />
                  </div>
                </template>

                <template #explanation-rating>
                  <v-expand-transition>
                    <div v-if="shouldShowExplanationRating" class="mt-4">
                      <ContentRatingPrompt
                        label="Rate this article"
                        :feedback-options="explanationRatingFeedbackOptions"
                        :loading="explanationRatingSubmitting"
                        @rate="handleExplanationRating"
                        @dismiss="dismissExplanationRating"
                      />
                    </div>
                  </v-expand-transition>
                </template>
              </QuestionPreview>
            </v-col>

            <v-col
              v-if="showResults && showElements"
              cols="12"
              v-motion
              :initial="{ opacity: 0, y: 10 }"
              :enter="{ opacity: 1, y: 0, scale: 1 }"
              :delay="200"
            >
              <div class="pa-2 bg-slightBlue rounded-lg elevation-0">
                <div class="mb-4">
                  <div
                    class="d-flex flex-column flex-sm-row justify-space-between align-sm-center"
                  >
                    <div class="text-h6 text-xs-subtitle-1 mb-2 mb-sm-0">
                      Discussion Section
                    </div>
                    <div class="d-flex align-center mt-4 mt-sm-0">
                      <v-btn
                        icon="mdi-cog"
                        variant="text"
                        size="small"
                        color="primary"
                        class="mr-2"
                        @click="openCommentSettingsDialog"
                      ></v-btn>
                      <v-select
                        v-model="commentOrder"
                        :items="[
                          { title: 'Latest', value: 'latest' },
                          { title: 'Most Helpful', value: 'helpful' },
                        ]"
                        label="Sort by"
                        density="compact"
                        hide-details
                        variant="outlined"
                        class="comment-order-select"
                        style="max-width: 150px"
                      ></v-select>
                    </div>
                  </div>
                </div>
                <v-divider class="mb-6"></v-divider>

                <!-- Comment input at the top -->
                <v-textarea
                  v-model="newComment"
                  label="Add a comment"
                  variant="outlined"
                  append-inner-icon="mdi-send"
                  @click:append-inner="addComment"
                  @keydown.meta.enter="addComment"
                  @keydown.ctrl.enter="addComment"
                  class="rounded-lg mb-6"
                  rows="2"
                  auto-grow
                  no-resize
                  :loading="commentLoading"
                  :disabled="commentLoading"
                ></v-textarea>

                <div v-if="fetchingComments" class="text-center py-12">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="64"
                    class="mb-6"
                  ></v-progress-circular>
                  <div class="text-h6 text-xs-subtitle-1 text-grey-darken-1">
                    Loading comments...
                  </div>
                </div>

                <div
                  v-else-if="comments.length === 0"
                  class="text-center py-12"
                >
                  <v-icon
                    icon="mdi-comment-outline"
                    size="64"
                    color="grey lighten-1"
                    class="mb-6"
                  ></v-icon>
                  <div class="text-h6 text-xs-subtitle-1 text-grey-darken-1">
                    No comments yet
                  </div>
                  <div
                    class="text-body-2 text-xs-caption text-grey-darken-1 mt-3"
                  >
                    Be the first to start the discussion!
                  </div>
                </div>

                <div v-else class="bg-transparent">
                  <div
                    v-for="comment in sortedComments"
                    :key="comment.id"
                    class="mb-12 rounded-lg"
                  >
                    <div class="d-flex align-start mb-4">
                      <button
                        v-if="canOpenCommentAuthorContact(comment)"
                        type="button"
                        class="comment-author-profile-avatar mr-4"
                        :aria-label="`Open ${getCommentDisplayName(
                          comment,
                          isAdmin,
                        )} contact details`"
                        @click="openCommentAuthorContact(comment)"
                      >
                        <v-avatar
                          :color="comment.color || 'primary'"
                          :size="avatarSize"
                        >
                          <v-img
                            v-if="comment.avatar"
                            :src="comment.avatar"
                          ></v-img>
                          <span v-else class="text-body-2 text-xs-caption">{{
                            getInitials(getCommentDisplayName(comment, isAdmin))
                          }}</span>
                        </v-avatar>
                      </button>
                      <v-avatar
                        v-else
                        :color="comment.color || 'primary'"
                        :size="avatarSize"
                        class="mr-4"
                      >
                        <v-img
                          v-if="comment.avatar"
                          :src="comment.avatar"
                        ></v-img>
                        <span v-else class="text-body-2 text-xs-caption">{{
                          getInitials(getCommentDisplayName(comment, isAdmin))
                        }}</span>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div
                          class="text-caption text-md-body-1 font-weight-medium d-flex align-center flex-wrap"
                        >
                          <button
                            v-if="canOpenCommentAuthorContact(comment)"
                            type="button"
                            class="comment-author-profile-name"
                            @click="openCommentAuthorContact(comment)"
                          >
                            {{
                              mobile
                                ? getCommentDisplayShortName(comment, isAdmin)
                                : getCommentDisplayName(comment, isAdmin)
                            }}
                          </button>
                          <span v-else>{{
                            mobile
                              ? getCommentDisplayShortName(comment, isAdmin)
                              : getCommentDisplayName(comment, isAdmin)
                          }}</span>
                          <v-chip
                            v-if="isAdmin && isCommentAuthorNameHidden(comment)"
                            label
                            color="grey"
                            variant="tonal"
                            size="small"
                            class="mx-2"
                          >
                            Hidden from others
                          </v-chip>
                          <v-chip
                            v-if="comment.byAdmin"
                            label
                            color="primary"
                            size="small"
                            class="mx-2"
                          >
                            Admin
                          </v-chip>
                          <v-chip
                            v-if="comment.byAi"
                            label
                            color="info"
                            size="small"
                            class="mx-2"
                          >
                            AI Response
                          </v-chip>
                          <span class="mx-2 text-grey-darken-1">•</span>
                          <span
                            class="text-caption text-xs-overline text-grey-darken-1"
                          >
                            {{ formatDateTime(comment.created) }}
                          </span>
                          <span
                            v-if="comment.edited && !mobile"
                            class="text-caption text-xs-overline text-grey-darken-1 ml-2"
                          >
                            (edited)
                          </span>
                          <span
                            v-if="
                              comment.isCurrentUserAuthor && !comment.isDeleted
                            "
                            class="mx-2 text-grey-darken-1"
                            >•</span
                          >
                          <v-btn
                            v-if="
                              comment.isCurrentUserAuthor && !comment.isDeleted
                            "
                            icon="mdi-pencil"
                            size="x-small"
                            variant="text"
                            color="progress"
                            @click="toggleEditMode(comment.id)"
                          ></v-btn>
                          <v-btn
                            v-if="
                              (comment.isCurrentUserAuthor || isAdmin) &&
                              !comment.isDeleted
                            "
                            icon="mdi-delete"
                            size="x-small"
                            variant="text"
                            color="error"
                            @click="deleteComment(comment.id)"
                          ></v-btn>
                        </div>
                        <div
                          v-if="editingCommentId !== comment.id"
                          style="white-space: pre-wrap"
                          class="text-body-2 text-xs-caption mt-2 mb-4"
                        >
                          {{ comment.isDeleted ? "[deleted]" : comment.text }}
                        </div>
                        <v-textarea
                          v-else
                          v-model="comment.text"
                          label="Edit comment"
                          variant="outlined"
                          density="compact"
                          append-inner-icon="mdi-check"
                          @click:append-inner="
                            editComment(comment.id, comment.text)
                          "
                          @keydown.meta.enter="
                            editComment(comment.id, comment.text)
                          "
                          @keydown.ctrl.enter="
                            editComment(comment.id, comment.text)
                          "
                          class="mt-2 mb-4"
                          rows="3"
                          auto-grow
                          no-resize
                        ></v-textarea>
                        <div class="d-flex flex-wrap">
                          <v-btn
                            variant="text"
                            density="comfortable"
                            size="small"
                            :color="comment.userHasUpvoted ? 'progress' : ''"
                            :disabled="upvoteLoading[comment.id]"
                            @click="toggleUpvote(comment.id)"
                            class="mr-4 mb-2"
                          >
                            <v-icon
                              :icon="
                                comment.userHasUpvoted
                                  ? 'mdi-thumb-up'
                                  : 'mdi-thumb-up-outline'
                              "
                              :color="comment.userHasUpvoted ? 'progress' : ''"
                              class="mr-2"
                            />
                            {{ comment.likes || 0 }}
                            <span class="ml-1 d-none d-sm-inline">Helpful</span>
                          </v-btn>
                          <v-btn
                            variant="text"
                            density="comfortable"
                            size="small"
                            @click="toggleReplyInput(comment.id)"
                            class="mb-2"
                            color="progress"
                          >
                            <v-icon icon="mdi-reply" class="mr-2" />
                            Reply
                          </v-btn>
                          <v-btn
                            v-if="isAdmin"
                            :variant="comment?.resolved ? 'flat' : 'outlined'"
                            density="comfortable"
                            size="small"
                            @click="toggleResolved(comment.id)"
                            class="mb-2 ml-4"
                            color="green"
                          >
                            <v-icon
                              :icon="
                                comment?.resolved
                                  ? 'mdi-check-circle'
                                  : 'mdi-check-circle-outline'
                              "
                              class="mr-2"
                            />
                            {{
                              comment?.resolved
                                ? "Mark Unresolved"
                                : "Mark Resolved"
                            }}
                          </v-btn>
                        </div>
                        <!-- Reply input -->
                        <v-textarea
                          v-if="comment.showReplyInput"
                          v-model="comment.replyText"
                          label="Write a reply"
                          variant="outlined"
                          density="compact"
                          append-inner-icon="mdi-send"
                          @click:append-inner="addReply(comment.id)"
                          @keydown.meta.enter.prevent="addReply(comment.id)"
                          @keydown.ctrl.enter.prevent="addReply(comment.id)"
                          class="mt-4"
                          rows="2"
                          auto-grow
                          no-resize
                          :loading="replyLoading[comment.id]"
                          :disabled="replyLoading[comment.id]"
                          :ref="
                            (el) => {
                              if (el) replyTextareas[comment.id] = el;
                            }
                          "
                        ></v-textarea>
                      </div>
                    </div>

                    <!-- Replies -->
                    <div
                      v-for="reply in sortedReplies(comment.replies)"
                      :key="reply.id"
                      class="ml-12 mt-6"
                    >
                      <div class="d-flex align-start">
                        <button
                          v-if="canOpenCommentAuthorContact(reply)"
                          type="button"
                          class="comment-author-profile-avatar mr-4"
                          :aria-label="`Open ${getCommentDisplayName(
                            reply,
                            isAdmin,
                          )} contact details`"
                          @click="openCommentAuthorContact(reply)"
                        >
                          <v-avatar
                            :color="reply.color || 'primary'"
                            :size="avatarSize"
                          >
                            <v-img
                              v-if="reply.avatar"
                              :src="reply.avatar"
                            ></v-img>
                            <span v-else class="text-body-2 text-xs-caption">{{
                              getInitials(getCommentDisplayName(reply, isAdmin))
                            }}</span>
                          </v-avatar>
                        </button>
                        <v-avatar
                          v-else
                          :color="reply.color || 'primary'"
                          :size="avatarSize"
                          class="mr-4"
                        >
                          <v-img
                            v-if="reply.avatar"
                            :src="reply.avatar"
                          ></v-img>
                          <span v-else class="text-body-2 text-xs-caption">{{
                            getInitials(getCommentDisplayName(reply, isAdmin))
                          }}</span>
                        </v-avatar>
                        <div class="flex-grow-1">
                          <div
                            class="text-caption text-md-body-1 font-weight-medium d-flex align-center flex-wrap"
                          >
                            <button
                              v-if="canOpenCommentAuthorContact(reply)"
                              type="button"
                              class="comment-author-profile-name"
                              @click="openCommentAuthorContact(reply)"
                            >
                              {{
                                mobile
                                  ? getCommentDisplayShortName(reply, isAdmin)
                                  : getCommentDisplayName(reply, isAdmin)
                              }}
                            </button>
                            <span v-else>{{
                              mobile
                                ? getCommentDisplayShortName(reply, isAdmin)
                                : getCommentDisplayName(reply, isAdmin)
                            }}</span>
                            <v-chip
                              v-if="
                                isAdmin && isCommentAuthorNameHidden(reply)
                              "
                              label
                              color="grey"
                              variant="tonal"
                              size="small"
                              class="mx-2"
                            >
                              Hidden from others
                            </v-chip>
                            <v-chip
                              v-if="reply.byAdmin"
                              label
                              color="primary"
                              size="small"
                              class="mx-2"
                            >
                              Admin
                            </v-chip>
                            <v-chip
                              v-if="reply.byAi"
                              label
                              color="info"
                              size="small"
                              class="mx-2"
                            >
                              AI Response
                            </v-chip>
                            <span class="mx-2 text-grey-darken-1">•</span>
                            <span
                              class="text-caption text-xs-overline text-grey-darken-1"
                            >
                              {{ formatDateTime(reply.created) }}
                            </span>
                            <span
                              v-if="reply.edited && !mobile"
                              class="text-caption text-xs-overline text-grey-darken-1 ml-2"
                            >
                              (edited)
                            </span>
                            <span
                              v-if="reply.isCurrentUserAuthor"
                              class="mx-2 text-grey-darken-1"
                              >•</span
                            >
                            <v-btn
                              v-if="reply.isCurrentUserAuthor"
                              icon="mdi-pencil"
                              size="x-small"
                              variant="text"
                              color="progress"
                              @click="toggleEditMode(reply.id)"
                            ></v-btn>
                            <v-btn
                              v-if="reply.isCurrentUserAuthor || isAdmin"
                              icon="mdi-delete"
                              size="x-small"
                              variant="text"
                              color="error"
                              @click="deleteComment(reply.id, true)"
                            ></v-btn>
                          </div>
                          <div
                            v-if="editingCommentId !== reply.id"
                            style="white-space: pre-wrap"
                            class="text-body-2 text-xs-caption mt-2 mb-4"
                          >
                            {{ reply.text }}
                          </div>
                          <v-textarea
                            v-else
                            v-model="reply.text"
                            label="Edit reply"
                            variant="outlined"
                            density="compact"
                            append-inner-icon="mdi-check"
                            @click:append-inner="
                              editComment(reply.id, reply.text)
                            "
                            @keydown.meta.enter="
                              editComment(reply.id, reply.text)
                            "
                            @keydown.ctrl.enter="
                              editComment(reply.id, reply.text)
                            "
                            class="mt-2 mb-4"
                            rows="3"
                            auto-grow
                            no-resize
                          ></v-textarea>
                          <div class="d-flex align-center">
                            <v-btn
                              variant="text"
                              density="comfortable"
                              size="small"
                              :color="reply.userHasUpvoted ? 'progress' : ''"
                              :disabled="upvoteLoading[reply.id]"
                              @click="toggleUpvote(reply.id)"
                            >
                              <v-icon
                                :icon="
                                  reply.userHasUpvoted
                                    ? 'mdi-thumb-up'
                                    : 'mdi-thumb-up-outline'
                                "
                                :color="reply.userHasUpvoted ? 'progress' : ''"
                                class="mr-2"
                              />
                              {{ reply.likes || 0 }}
                              <span class="ml-1 d-none d-sm-inline"
                                >Helpful</span
                              >
                            </v-btn>
                            <v-btn
                              v-if="isAdmin"
                              :variant="reply?.resolved ? 'flat' : 'outlined'"
                              density="comfortable"
                              size="small"
                              @click="toggleResolved(comment.id, reply.id)"
                              class="ml-4"
                              color="green"
                            >
                              <v-icon
                                :icon="
                                  reply?.resolved
                                    ? 'mdi-check-circle'
                                    : 'mdi-check-circle-outline'
                                "
                                class="mr-2"
                              />
                              {{
                                reply?.resolved
                                  ? "Mark Unresolved"
                                  : "Mark Resolved"
                              }}
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <div
      v-if="!showOverview && !isSingleQuestion"
      class="action-pane d-flex justify-end bg-bw pt-3 px-3 elevation-12"
    >
      <v-btn
        v-if="sessionData?.status === 'completed'"
        :loading="loading"
        @click="showOverview = true"
        color="primary"
        rounded="lg"
        variant="flat"
        :size="mobile ? 'default' : 'x-large'"
        :class="mobile ? 'mx-1' : 'mx-3'"
        v-motion
        :initial="{ opacity: 0, x: -20 }"
        :enter="{ opacity: 1, x: 0, scale: 1 }"
      >
        Show Overview
      </v-btn>
      <v-btn
        v-else-if="
          answeredAll || questionIndex === sessionData?.questions.length - 1
        "
        :loading="loading"
        @click="checkUnanswered"
        color="primary"
        rounded="lg"
        variant="flat"
        :size="mobile ? 'default' : 'x-large'"
        :class="mobile ? 'mx-1' : 'mx-3'"
        v-motion
        :initial="{ opacity: 0, x: -20 }"
        :enter="{ opacity: 1, x: 0, scale: 1 }"
      >
        Finish
      </v-btn>
      <v-btn
        :loading="loading"
        @click="handleProgress('back')"
        :disabled="answerSaving || questionIndex === 0"
        color="primary"
        rounded="lg"
        variant="flat"
        :size="mobile ? 'default' : 'x-large'"
        :class="mobile ? 'mx-1' : 'mx-3'"
        v-motion
        :initial="{ opacity: 0, x: 20 }"
        :enter="{ opacity: 1, x: 0, scale: 1 }"
        :delay="100"
      >
        Back
      </v-btn>
      <v-btn
        :loading="loading"
        @click="handleProgress('next')"
        :disabled="answerSaving || questionIndex === sessionData?.questions.length - 1"
        color="primary"
        rounded="lg"
        variant="flat"
        :size="mobile ? 'default' : 'x-large'"
        :class="mobile ? 'mx-1' : 'mx-3'"
        v-motion
        :initial="{ opacity: 0, x: 20 }"
        :enter="{ opacity: 1, x: 0, scale: 1 }"
        :delay="150"
      >
        Next

        <template v-slot:append>
          <v-tooltip location="top" content-class="bg-info">
            <div class="text-caption pa-1">
              <v-icon class="mr-2" x-small>mdi-information</v-icon>
              You can use the arrow keys to navigate between questions
            </div>
            <template v-slot:activator="{ props }">
              <v-img
                v-bind="props"
                class="hidden-sm-and-down"
                width="21"
                height="21"
                src="@/assets/arrows.svg"
              ></v-img>
            </template>
          </v-tooltip>
        </template>
      </v-btn>
    </div>
  </v-container>
  <v-dialog
    v-model="showUnansweredDialog"
    min-height="400"
    transition="dialog-bottom-transition"
    width="500"
  >
    <v-card class="pa-3">
      <v-toolbar class="rounded-lg pl-6" color="primary">
        You have unanswered questions!</v-toolbar
      >
      <v-card-text class="py-12 text-body-1">
        <p class="mb-3">
          If you finish now, unanswered questions will score zero for this
          session. They will remain <strong>unattempted</strong> and available
          in new sessions.
        </p>
        <p class="mb-3">
          Alternatively, you can close the page and return later. Your progress
          will be saved.
        </p>
        <p>
          Please note that in test mode, the timer will continue to run even if
          you close the page.
        </p>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn color="primary" rounded="lg" variant="flat" @click="endSession"
          >Continue</v-btn
        >
        <v-btn
          variant="text"
          color="error"
          rounded="lg"
          @click="showUnansweredDialog = false"
          >Close</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <CommentSettingsDialog
    v-model="showCommentSettingsDialog"
    :hide-name="currentUserHideName"
    :loading="commentSettingsSaving"
    @save="saveCommentSettings"
  />

  <CommentAuthorContactDialog
    v-model="commentAuthorContactOpen"
    :contact="commentAuthorContact"
    :loading="commentAuthorContactLoading"
    :error="commentAuthorContactError"
  />

  <!-- AI Chat Interface (Admin Only) -->
  <AiChatInterface
    class="session-ai-chat"
    :currentQuestionData="currentQuestionData"
    :questionBank="questionBank"
    :images="aiChatImages"
    desktop-dock="left"
    compact-bottom
  >
  </AiChatInterface>
</template>

<script setup>
import { useBankProgress } from "@/composables/useBankProgress";
import { isResetSessionForOwner } from "@/utils/bankProgress.mjs";
import {
  ref,
  computed,
  watch,
  reactive,
  onMounted,
  onUnmounted,
  nextTick,
} from "vue";
import { useDisplay } from "vuetify";
import { useAlertStore } from "@/store/alert";
import { useStatsStore } from "@/store/stats";
import { useDataStore } from "@/store/data";
import { useUserStore } from "@/store/user";
import { usePaymentStore } from "@/store/payment";
import { useAdminAccessStore } from "@/store/adminAccess";
const alertStore = useAlertStore();
const statsStore = useStatsStore();
const dataStore = useDataStore();
const paymentStore = usePaymentStore();
const userStore = useUserStore();
const adminAccessStore = useAdminAccessStore();
const currentUserLevel = computed(() => adminAccessStore.level);
const isAdmin = computed(() => adminAccessStore.isAdmin);

import AiChatInterface from "@/components/AiChatInterface.vue";
import CommentSettingsDialog from "@/components/CommentSettingsDialog.vue";
import CommentAuthorContactDialog from "@/components/admin/CommentAuthorContactDialog.vue";
import AdminDraftPreviewApprovalActions from "@/components/admin/AdminDraftPreviewApprovalActions.vue";
import QuestionPreview from "@/components/question-bank/QuestionPreview.vue";
import { useQuestionApprovalValidation } from "@/composables/questionApprovalValidation";
import { useAdminDraftPreviewApproval } from "@/composables/useAdminDraftPreviewApproval";
import { useCommentAuthorContact } from "@/composables/useCommentAuthorContact";
import {
  loadCurrentUserCommentHideName,
  useCommentPrivacy,
} from "@/composables/useCommentPrivacy";
import ContentRatingPrompt from "@/components/ContentRatingPrompt.vue";
import { getContentRatingFeedbackOptions } from "@/utils/contentRatingFeedback.mjs";
import {
  normalizeRatingPayload,
  saveContentRating,
} from "@/utils/contentRatingPersistence";
import {
  hasRecentRating,
  recordRating,
  isValidThumbValue,
} from "@/utils/ratingHelpers";
import { sanitizeExplanationHtml } from "@/utils/explanationHtmlSanitizer";
import {
  DRAFT_APPROVED_ELSEWHERE_MESSAGE,
  buildDraftApprovedRedirect,
} from "@/utils/draftAvailability.mjs";
import { ADMIN_DRAFT_QUEUE_KIND_CONTENT } from "@/utils/adminDraftQueue.mjs";
import { orderRepliesChronologically } from "@/utils/commentOrdering.mjs";
import { shuffleAnswersForSession } from "@/utils/seededAnswerOrder.mjs";
import { useRoute, useRouter, onBeforeRouteLeave } from "vue-router";
const loading = ref(true);
const showOverview = ref(false);
const route = useRoute();
const router = useRouter();
// Get the session or question ID from the route
const sessionId = route.params.id; // could be question id too

const questionIndex = ref(0); // the current question index
let questionsData = ref({}); // the questions data
const copyQuestionLinkLoading = ref(false);
const ratingVersion = ref(0);
const questionRatingDismissed = ref(false);
const explanationRatingDismissed = ref(false);
const questionRatingSubmitting = ref(false);
const explanationRatingSubmitting = ref(false);
const questionRatingFeedbackOptions = getContentRatingFeedbackOptions("question");
const explanationRatingFeedbackOptions =
  getContentRatingFeedbackOptions("explanation");

const extractRefId = (refLike) => {
  if (!refLike) return null;
  if (typeof refLike === "string") return refLike;
  if (typeof refLike?.id === "string") return refLike.id;
  if (typeof refLike?.path === "string") {
    const segments = refLike.path.split("/");
    return segments[segments.length - 1] || null;
  }
  return null;
};

// Add bank ref
const questionBank = ref(route.params.bank || "part-2-question-bank");
const collectionPath = computed(() => {
  if (questionBank.value === "part-2-question-bank") {
    return ""; // Use default collections
  } else if (questionBank.value === "part-1-question-bank") {
    return "new_modules/part-1-question-bank/";
  } else if (questionBank.value === "duke-elder-question-bank") {
    return "new_modules/duke-elder-question-bank/";
  } else {
    // it is using the default question bank which is part-2-question-bank
    return "";
  }
});

// Flags and question stats belong to the open session, not the dashboard bank.
const sessionUserQuestions = computed(() => {
  const statsName = collectionPath.value.includes("part-1-question-bank")
    ? "part1Stats"
    : collectionPath.value.includes("duke-elder-question-bank")
      ? "dukeElderStats"
      : "stats";
  const questions = statsStore.userData?.[statsName]?.questions || {};
  return Object.entries(questions).map(([qId, stats]) => ({ ...stats, qId }));
});
const sessionFlaggedQuestionIds = computed(() =>
  sessionUserQuestions.value.filter((question) => question.flagged).map((question) => question.qId),
);

// Helper function to chunk the array
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const numberOfQuestionsLoading = ref(0);
const numberOfQuestionsTotal = ref(0);

const getAllQuestionData = async (questionIds) => {
  const questionDataLocal = {};
  const chunkSize = 10; // Firestore in query supports up to 30 comparison values

  try {
    // Split questionIds into chunks of 30
    const chunks = chunkArray(questionIds, chunkSize);
    let current = 0;
    numberOfQuestionsTotal.value = questionIds.length;
    // Add a cache object outside the loop
    const explanationCache = {};
    for (const chunk of chunks) {
      current += chunk.length;
      numberOfQuestionsLoading.value = current;
      const q = query(
        collection(db, `${collectionPath.value}questions`),
        where("__name__", "in", chunk),
      );

      const querySnapshot = await getDocs(q);

      // Process the query results
      for (const doc of querySnapshot.docs) {
        const questionDocData = doc.data();
        questionDataLocal[doc.id] = {
          ...questionDocData,
          lastUpdated: questionDocData?.lastUpdated || null,
          newAnswerImageCaptions: questionDocData?.newAnswerImageCaptions || {},
        };
        // Default explanation version marker
        questionDataLocal[doc.id].explanationLastUpdated = null;
        // Fetch explanation if explanationRef exists
        if (doc.data().explanationRef) {
          const refId = doc.data().explanationRef.id; // Get the reference ID

          try {
            // Check if we already have this explanation in cache
            if (explanationCache[refId]) {
              questionDataLocal[doc.id].answerExplanationHTML =
                explanationCache[refId].html;
              questionDataLocal[doc.id].answerTitle =
                explanationCache[refId].title;
              questionDataLocal[doc.id].universalImages =
                explanationCache[refId].universalImages;
              questionDataLocal[doc.id].universalImagesCols =
                explanationCache[refId].universalImagesCols;
              questionDataLocal[doc.id].universalImageCaptions =
                explanationCache[refId].universalImageCaptions || {};
              questionDataLocal[doc.id].explanationLastUpdated =
                explanationCache[refId].lastUpdated || null;
            } else {
              // If not in cache, fetch and store it
              const explanationDoc = await getDoc(doc.data().explanationRef);
              if (explanationDoc.exists()) {
                const expData = explanationDoc.data();
                const html = expData.html;
                const title = expData.title;
                const universalImages = expData.newAnswerImageIds;

                const universalImagesCols = expData.newAnswerImageCols;
                const universalImageCaptions =
                  expData.newAnswerImageCaptions || {};
                explanationCache[refId] = {
                  html,
                  title,
                  universalImages,
                  universalImagesCols,
                  universalImageCaptions,
                  lastUpdated: expData.lastUpdated || null,
                }; // Store in cache
                questionDataLocal[doc.id].answerExplanationHTML = html;
                questionDataLocal[doc.id].answerTitle = title;
                questionDataLocal[doc.id].universalImages = universalImages;
                questionDataLocal[doc.id].universalImagesCols =
                  universalImagesCols;
                questionDataLocal[doc.id].universalImageCaptions =
                  universalImageCaptions;
                questionDataLocal[doc.id].explanationLastUpdated =
                  expData.lastUpdated || null;
              }
            }
          } catch (error) {
            console.error(
              `Error fetching explanation for question ${doc.id}:`,
              error,
            );
            questionDataLocal[doc.id].answerExplanationHTML =
              "<p>Error loading article.</p>";
            questionDataLocal[doc.id].answerTitle = "";
          }
        }
      }
    }
  } catch (error) {
    console.error("An error occurred while fetching question data:", error);
    alertStore.showAlert(
      "There was an error fetching some questions. Please check your internet connection.",
      "error",
    );
    return;
  }

  return questionDataLocal;
};

const isSingleQuestion = computed(() => {
  return route.name === "Question Page";
});

// Get the session data from the database
import { useDocument, useFirestore, useCurrentUser } from "vuefire";
const currentUser = useCurrentUser();
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
const db = useFirestore();

let sessionData = ref(null);

const currentQuestionId = computed(() => {
  if (isSingleQuestion.value) {
    const paramId = route.params.id;
    return typeof paramId === "string" ? paramId : null;
  }

  const questions = sessionData.value?.questions;
  if (!Array.isArray(questions) || questions.length === 0) {
    return null;
  }

  return questions[questionIndex.value]?.questionId || null;
});

const currentQuestionBank = computed(() => {
  if (typeof route.params.bank === "string" && route.params.bank.length > 0) {
    return route.params.bank;
  }

  return questionBank.value || "part-2-question-bank";
});
const bankProgress = useBankProgress(currentQuestionBank);
const progressReadOnly = computed(
  () =>
    !isSingleQuestion.value &&
    !!sessionData.value?.userId &&
    isResetSessionForOwner(sessionData.value, bankProgress.value, currentUser.value?.uid),
);

const copyTextToClipboard = async (text) => {
  if (
    typeof navigator !== "undefined" &&
    navigator?.clipboard?.writeText instanceof Function
  ) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard API is not available");
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "absolute";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  const successful = document.execCommand("copy");
  document.body.removeChild(textArea);

  if (!successful) {
    throw new Error("Fallback clipboard copy failed");
  }
};

const copyQuestionLink = async () => {
  const questionId = currentQuestionId.value;
  if (!questionId) {
    alertStore.showAlert("Question link unavailable.", "error");
    return;
  }

  const origin =
    typeof window !== "undefined" && window?.location?.origin
      ? window.location.origin
      : "";
  if (!origin) {
    alertStore.showAlert("Question link unavailable.", "error");
    return;
  }

  const bankSlug = currentQuestionBank.value || "part-2-question-bank";
  const directRoute = router.resolve({
    name: "Question Page",
    params: {
      bank: bankSlug,
      id: questionId,
    },
    query: forceDraft.value ? { forceDraft: "true" } : undefined,
  });
  const directLink = `${origin}${directRoute.href}`;

  try {
    copyQuestionLinkLoading.value = true;
    await copyTextToClipboard(directLink);
    alertStore.showAlert("Direct link copied to clipboard.", "success");
  } catch (error) {
    console.error("Failed to copy direct question link:", error);
    alertStore.showAlert("Unable to copy link. Please try again.", "error");
  } finally {
    copyQuestionLinkLoading.value = false;
  }
};

const dismissQuestionRating = () => {
  questionRatingDismissed.value = true;
};

const dismissExplanationRating = () => {
  explanationRatingDismissed.value = true;
};

const handleQuestionRating = async (payload) => {
  const { value } = normalizeRatingPayload(payload);
  if (!isValidThumbValue(value) || questionRatingSubmitting.value) return;
  const questionId = currentQuestionId.value;
  if (
    !questionId ||
    isSingleQuestion.value ||
    forceDraft.value ||
    showOverview.value
  ) {
    return;
  }
  const questionVersion = currentQuestionVersion.value;

  try {
    questionRatingSubmitting.value = true;
    const questionRef = doc(
      collection(db, `${collectionPath.value}questions`),
      questionId,
    );
    await saveContentRating({
      db,
      targetRef: questionRef,
      contentType: "question",
      contentId: questionId,
      contentVersion: questionVersion,
      payload,
      uid: currentUser.value?.uid,
    });
    recordRating("question", questionId, questionVersion);
    ratingVersion.value += 1;
    if (currentQuestionId.value === questionId) {
      questionRatingDismissed.value = true;
    }
  } catch (error) {
    console.error("Error saving question rating:", error);
    alertStore.showAlert(
      "Couldn't save your rating. Please try again.",
      "error",
    );
  } finally {
    questionRatingSubmitting.value = false;
  }
};

const handleExplanationRating = async (payload) => {
  const { value } = normalizeRatingPayload(payload);
  if (!isValidThumbValue(value) || explanationRatingSubmitting.value) return;
  const explanationId = explanationDocId.value;
  if (!explanationId || isSingleQuestion.value || forceDraft.value) return;
  const explanationVersion = currentExplanationVersion.value;

  try {
    explanationRatingSubmitting.value = true;
    const explanationRef = doc(db, "explanations", explanationId);
    await saveContentRating({
      db,
      targetRef: explanationRef,
      contentType: "explanation",
      contentId: explanationId,
      contentVersion: explanationVersion,
      payload,
      uid: currentUser.value?.uid,
    });
    recordRating("explanation", explanationId, explanationVersion);
    ratingVersion.value += 1;
    if (explanationDocId.value === explanationId) {
      explanationRatingDismissed.value = true;
    }
  } catch (error) {
    console.error("Error saving explanation rating:", error);
    alertStore.showAlert(
      "Couldn't save your rating. Please try again.",
      "error",
    );
  } finally {
    explanationRatingSubmitting.value = false;
  }
};

const draftExists = ref(false);
const forceDraft = computed(() => route.query.forceDraft === "true");
let draftNoLongerAvailableHandled = false;

const redirectFromApprovedDraft = async ({
  refreshPublishedQuestion = false,
} = {}) => {
  // Our approval action owns navigation when its draft disappears.
  if (isApprovalPending.value || approvalCompleted.value) return true;
  if (draftNoLongerAvailableHandled) return true;

  if (refreshPublishedQuestion) {
    const publishedQuestionData = await getAllQuestionData([route.params.id]);
    if (Object.keys(publishedQuestionData).length === 0) {
      return false;
    }
    questionsData = ref(publishedQuestionData);
    await processImages(publishedQuestionData[route.params.id]);
  }

  draftNoLongerAvailableHandled = true;
  draftExists.value = false;
  alertStore.showAlert(DRAFT_APPROVED_ELSEWHERE_MESSAGE, "info");
  await router.replace(
    buildDraftApprovedRedirect({
      routeName: route.name,
      params: route.params,
      query: route.query,
    }),
  );
  return true;
};

if (!isSingleQuestion.value) {
  const { data, pending, error, promise } = useDocument(
    doc(collection(db, `${collectionPath.value}sessions`), sessionId),
  );

  sessionData = data;

  promise.value.then(async () => {
    // All existing session validation logic
    if (!sessionData.value) {
      alertStore.showAlert("Session Not Found.", "error");
      router.push("/sessions");
    }

    if (sessionData.value.userId !== currentUser.value.uid) {
      alertStore.showAlert("Unauthorised.", "error");
      router.push("/sessions");
    }

    const isFirstSession =
      dataStore.sessionsDataOrdered[dataStore.sessionsDataOrdered.length - 1]
        ?.id === sessionId;
    if (
      !paymentStore.isSubscribed?.[
        questionBank?.value ? questionBank?.value : "part-2-question-bank"
      ]?.val &&
      !isFirstSession
    ) {
      alertStore.showAlert("Subscribe To Access Session.", "error");
      router.push("/");
    }

    questionIndex.value = sessionData.value.currentQuestionIndex;
    const allQuestionIds = sessionData.value.questions.map((q) => q.questionId);
    questionsData.value = await getAllQuestionData(allQuestionIds);

    // Image processing logic
    const questionsDataValues = Object.values(questionsData.value);
    const qImgPaths = questionsDataValues.flatMap((q) => {
      const optionImages = Array.isArray(q.options)
        ? q.options
            .map((opt) => getOptionImageId(opt))
            .filter((id) => typeof id === "string" && id.trim().length > 0)
        : [];

      return [
        ...(q.newQuestionImageIds || []),
        ...(q.newAnswerImageIds || []),
        ...(q.universalImages || []),
        ...optionImages,
      ];
    });

    const filteredImgPaths = [...new Set(qImgPaths.filter(Boolean))];
    await getUrls(filteredImgPaths);

    await bankProgress.promise.value;
    // Timer logic
    let remainingMs = null;
    if (
      sessionData.value.enableTimer &&
      sessionData.value.status !== "completed" &&
      !progressReadOnly.value
    ) {
      const existingEndMs =
        sessionData.value.timerEndAt?.seconds &&
        sessionData.value.timerEndAt.seconds * 1000;

      if (!existingEndMs) {
        const timerEndDate = new Date(
          Date.now() + sessionData.value.timerAmount * 60 * 1000,
        );
        remainingMs = Math.max(0, timerEndDate.getTime() - Date.now());
        try {
          await setDoc(
            doc(collection(db, `${collectionPath.value}sessions`), sessionId),
            {
              timerEndAt: timerEndDate,
            },
            { merge: true },
          );
        } catch (error) {
          alertStore.showAlert(
            "An error occurred. Please check your internet connection.",
            "error",
          );
          console.error("An error occurred:", error);
          return;
        }
      } else {
        remainingMs = Math.max(0, existingEndMs - Date.now());
      }
    }

    // Session completion checks
    if (sessionData.value.status === "completed") {
      showOverview.value = true;
    } else if (sessionData.value.enableTestMode) {
      const timeLeft =
        typeof remainingMs === "number" ? remainingMs : secondsRemaining.value;
      if (typeof timeLeft === "number" && timeLeft > 0) {
        setTimeout(() => {
          endSession();
        }, timeLeft);
      } else {
        endSession();
      }
    }

    setTimeout(() => {
      loading.value = false;
    }, 500);
  });
} else {
  (async () => {
    try {
      // First fetch the question data
      questionsData.value = await getAllQuestionData([route.params.id]);

      // Check subscription first
      if (
        !paymentStore.isSubscribed?.[
          questionBank?.value ? questionBank?.value : "part-2-question-bank"
        ]?.val
      ) {
        alertStore.showAlert("Subscribe To Access.", "error");
        router.push("/profile");
        return;
      }

      // Wait for admin check to complete
      await checkAdminStatus();

      // Check for draft if admin
      if (isAdmin.value) {
        const draftDocRef = doc(
          collection(db, `${collectionPath.value}drafts`),
          route.params.id,
        );
        const draftSnapshot = await getDoc(draftDocRef);
        draftExists.value = draftSnapshot.exists();
      }

      const hasQuestionData = Object.keys(questionsData.value).length > 0;
      const shouldLoadDraft =
        forceDraft.value ||
        (isAdmin.value && draftExists.value && !hasQuestionData);

      if (shouldLoadDraft && !forceDraft.value) {
        router.replace({
          name: "Question Page",
          params: route.params,
          query: {
            ...route.query,
            forceDraft: "true",
          },
        });
      }

      if (shouldLoadDraft) {
        if (!isAdmin.value) {
          alertStore.showAlert("Unauthorised", "error");
          router.push("/");
          return;
        }

        // Handle draft viewing logic
        const { data: draftDoc, promise } = useDocument(
          doc(collection(db, `${collectionPath.value}drafts`), route.params.id),
        );
        await promise.value;

        if (!draftDoc.value) {
          if (await advanceFromStaleDraftPreview()) {
            return;
          }
          if (hasQuestionData) {
            await redirectFromApprovedDraft();
          } else {
            alertStore.showAlert("Not found", "error");
            router.push("/");
            return;
          }
        } else {
          // Setup reactive draft data
          if (!draftDoc.value.explanationRef?.id) {
            questionsData = computed(() => ({
              [route.params.id]: {
                ...draftDoc.value,
                answerExplanationHTML: "",
                answerTitle: "",
                universalImages: [],
                universalImagesCols: 1,
                universalImageCaptions: {},
                explanationLastUpdated: draftDoc.value?.lastUpdated || null,
              },
            }));
          } else {
            const explanationRef = doc(
              db,
              "explanations",
              draftDoc.value.explanationRef.id,
            );
            const { data: explanationDoc, promise: explanationPromise } =
              useDocument(explanationRef);
            await explanationPromise.value;

            questionsData = computed(() => ({
              [route.params.id]: {
                ...draftDoc.value,
                answerExplanationHTML: explanationDoc.value?.html || "",
                answerTitle: explanationDoc.value?.title || "",
                universalImages: explanationDoc.value?.newAnswerImageIds || [],
                universalImagesCols:
                  explanationDoc.value?.newAnswerImageCols || 1,
                universalImageCaptions:
                  explanationDoc.value?.newAnswerImageCaptions || {},
                explanationLastUpdated:
                  explanationDoc.value?.lastUpdated ||
                  draftDoc.value?.lastUpdated ||
                  null,
              },
            }));

            // Watch for changes
            watch(
              [draftDoc, explanationDoc],
              async ([newDoc]) => {
                if (!newDoc) {
                  const redirected = await redirectFromApprovedDraft({
                    refreshPublishedQuestion: true,
                  });
                  if (!redirected) {
                    alertStore.showAlert("Question not found", "error");
                    router.push("/");
                  }
                  return;
                }
                await processImages(newDoc);
              },
              { deep: true },
            );
          }

          // Watch draft changes when no explanation
          if (!draftDoc.value.explanationRef?.path) {
            watch(
              draftDoc,
              async (newDoc) => {
                if (!newDoc) {
                  const redirected = await redirectFromApprovedDraft({
                    refreshPublishedQuestion: true,
                  });
                  if (!redirected) {
                    alertStore.showAlert("Question not found", "error");
                    router.push("/");
                  }
                  return;
                }
                await processImages(newDoc);
              },
              { deep: true },
            );
          }
        }
      } else {
        // Normal question fetching
        // questionsData.value = await getAllQuestionData([route.params.id]);
        if (!hasQuestionData) {
          alertStore.showAlert("Question not found", "error");
          router.push("/");
          return;
        }
      }

      // Initialize session data
      sessionData.value = {
        nonce: "single-question-preview",
        numberOfQuestions: 1,
        questions: [
          {
            questionId: route.params.id,
            chosenAnswerId: null,
            correct: null,
          },
        ],
        enableTestMode: false,
        status: "completed",
        currentQuestionIndex: 0,
        enableTimer: false,
      };

      // Process images
      await processImages(questionsData.value[route.params.id]);

      setTimeout(() => {
        loading.value = false;
      }, 500);
    } catch (error) {
      console.error("Error fetching question:", error);
      alertStore.showAlert(
        "Error loading question. Please check your internet connection.",
        "error",
      );
      router.push("/");
    }
  })();
}

const normalizeAnswerOptionImageId = (value) => {
  if (Array.isArray(value)) {
    return value[0] || "";
  }
  if (typeof value === "string") {
    return value;
  }
  return "";
};

const getOptionImageId = (option) => {
  if (!option || typeof option !== "object") return "";
  return normalizeAnswerOptionImageId(
    option.answerImageId ?? option.answerImageIds ?? "",
  );
};

const stripHtmlText = (value) => {
  if (typeof value !== "string") return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const buildOptionImageCaption = (option, index) => {
  const optionLabel = `Option ${index + 1}`;
  const optionText = stripHtmlText(option?.text || "");
  if (!optionText) {
    return `${optionLabel}: image answer option`;
  }
  if (optionText.length <= 90) {
    return `${optionLabel}: ${optionText}`;
  }
  return `${optionLabel}: ${optionText.slice(0, 87)}...`;
};

// Add this helper function for image processing
const processImages = async (questionData) => {
  if (!questionData) return;

  const optionImageIds = Array.isArray(questionData.options)
    ? questionData.options
        .map((opt) => getOptionImageId(opt))
        .filter((id) => typeof id === "string" && id.trim().length > 0)
    : [];

  const qImgPaths = [
    ...(questionData.newQuestionImageIds || []),
    ...(questionData.newAnswerImageIds || []),
    ...(questionData.universalImages || []),
    ...optionImageIds,
  ];

  const filteredImgPaths = [...new Set(qImgPaths.filter(Boolean))];

  if (filteredImgPaths.length > 0) {
    await getUrls(filteredImgPaths);
  }
};
// Important data for the page

// watch the questionIndex and whenever it changes update the db
watch(questionIndex, async (newVal, oldVal) => {
  if (newVal === oldVal || progressReadOnly.value || isSingleQuestion.value)
    return;
  await setDoc(
    doc(collection(db, `${collectionPath.value}sessions`), sessionId),
    {
      currentQuestionIndex: newVal,
    },
    { merge: true },
  );
});

const currentQuestionData = computed(() => {
  if (!sessionData.value) return {};
  return questionsData.value[
    sessionData.value.questions[questionIndex.value].questionId
  ];
});

const currentQuestionPreviewData = computed(() => {
  const question = currentQuestionData.value || {};
  const sessionQuestion =
    sessionData.value?.questions?.[questionIndex.value] || {};
  const options = Array.isArray(question.options)
    ? shuffleAnswersForSession(question.options, {
        sessionNonce: sessionData.value?.nonce || sessionId,
        questionId: sessionQuestion.questionId,
      })
    : [];

  return {
    ...question,
    options,
  };
});

const showUnansweredChip = computed(
  () =>
    showResults.value &&
    !isSingleQuestion.value &&
    !sessionData.value?.questions?.[questionIndex.value]?.chosenAnswerId,
);

const aiChatImages = computed(() => {
  const question = currentQuestionData.value;
  if (!question || typeof question !== "object") return [];

  const items = [];

  const pushId = (id, caption = null) => {
    if (!id) return;
    const url = allImgPaths[id] || null;
    if (!url) return;
    const normalizedCaption =
      typeof caption === "string" && caption.trim() ? caption.trim() : null;
    items.push({ url, caption: normalizedCaption });
  };

  const pushIds = (ids, captions) => {
    if (!Array.isArray(ids)) return;
    for (const id of ids) {
      if (!id) continue;
      const captionRaw =
        captions && typeof captions === "object" ? captions[id] : null;
      const caption =
        typeof captionRaw === "string" && captionRaw.trim() ? captionRaw : null;
      pushId(id, caption);
    }
  };

  // Question images (no captions supported)
  pushIds(question.newQuestionImageIds, null);

  // Explanation images: respect display order (top/bottom)
  const answerIds = Array.isArray(question.newAnswerImageIds)
    ? question.newAnswerImageIds
    : [];
  const answerCaptions =
    question.newAnswerImageCaptions &&
    typeof question.newAnswerImageCaptions === "object"
      ? question.newAnswerImageCaptions
      : {};

  const universalIds = Array.isArray(question.universalImages)
    ? question.universalImages
    : [];
  const universalCaptions =
    question.universalImageCaptions &&
    typeof question.universalImageCaptions === "object"
      ? question.universalImageCaptions
      : {};

  const answerLocation =
    question.answerImageLocation === "top" ? "top" : "bottom";

  if (answerLocation === "top") pushIds(answerIds, answerCaptions);
  pushIds(universalIds, universalCaptions);
  if (answerLocation !== "top") pushIds(answerIds, answerCaptions);

  const optionList = Array.isArray(question.options) ? question.options : [];
  optionList.forEach((option, index) => {
    const optionImageId = getOptionImageId(option);
    if (!optionImageId) return;
    pushId(optionImageId, buildOptionImageCaption(option, index));
  });

  // De-dupe by URL while preserving order; keep first caption unless later provides one
  const seen = new Map();
  const deduped = [];
  for (const img of items) {
    if (!img?.url) continue;
    if (!seen.has(img.url)) {
      const record = { ...img };
      seen.set(img.url, record);
      deduped.push(record);
      continue;
    }
    const existing = seen.get(img.url);
    if (!existing.caption && img.caption) {
      existing.caption = img.caption;
    }
  }

  return deduped;
});

const currentQuestionVersion = computed(
  () => currentQuestionData.value?.lastUpdated || null,
);

const currentExplanationVersion = computed(
  () => currentQuestionData.value?.explanationLastUpdated || null,
);

const hasExplanationContent = computed(() => {
  const universalHasContent =
    Boolean(currentQuestionData.value?.answerExplanationHTML) ||
    (Array.isArray(currentQuestionData.value?.universalImages) &&
      currentQuestionData.value.universalImages.length > 0) ||
    Boolean(currentQuestionData.value?.answerTitle);

  return universalHasContent || hasQuestionSpecificExplanation.value;
});

const sanitizedQuestionSpecificExplanationHTML = computed(() =>
  sanitizeExplanationHtml(
    currentQuestionData.value?.questionSpecificExplanationHTML || "",
  ),
);

const hasQuestionSpecificExplanation = computed(() => {
  const htmlContent = sanitizedQuestionSpecificExplanationHTML.value;

  if (typeof htmlContent !== "string") {
    return false;
  }

  const stripped = htmlContent
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, "")
    .replace(/\u00a0/g, "")
    .trim();

  return stripped.length > 0;
});

const explanationDocId = computed(() =>
  extractRefId(currentQuestionData.value?.explanationRef),
);

const explanationLastUpdatedDisplay = computed(() => {
  const source =
    currentQuestionData.value?.explanationLastUpdated ||
    currentQuestionData.value?.lastUpdated ||
    null;

  const dateObj = toDateSafe(source);
  if (!dateObj || !hasExplanationContent.value) return null;

  return {
    relative: formatDateTime(dateObj),
    absolute: formatAbsoluteDateTime(dateObj),
  };
});

watch(currentQuestionId, () => {
  questionRatingDismissed.value = false;
});

watch(explanationDocId, () => {
  explanationRatingDismissed.value = false;
});

const shouldShowQuestionRating = computed(() => {
  ratingVersion.value;
  const questionId = currentQuestionId.value;
  const questionVersion = currentQuestionVersion.value;
  if (
    !questionId ||
    isSingleQuestion.value ||
    forceDraft.value ||
    showOverview.value ||
    !showElements.value ||
    loading.value
  ) {
    return false;
  }

  if (!sessionData.value?.questions?.length) return false;
  if (questionRatingDismissed.value) return false;
  return !hasRecentRating("question", questionId, questionVersion);
});

const shouldShowExplanationRating = computed(() => {
  ratingVersion.value;
  const explanationId = explanationDocId.value;
  const explanationVersion = currentExplanationVersion.value;
  if (
    !explanationId ||
    isSingleQuestion.value ||
    forceDraft.value ||
    showOverview.value ||
    !showResults.value ||
    !showElements.value ||
    loading.value
  ) {
    return false;
  }

  if (!hasExplanationContent.value) return false;
  if (explanationRatingDismissed.value) return false;
  return !hasRecentRating("explanation", explanationId, explanationVersion);
});

const {
  isQuestionComplete: draftApprovalReady,
  unmetApprovalCriteria: draftApprovalIssues,
} = useQuestionApprovalValidation(currentQuestionData);

const draftPreviewCanApprove = computed(
  () =>
    isAdmin.value &&
    forceDraft.value &&
    isSingleQuestion.value &&
    Boolean(currentQuestionId.value),
);
const draftPreviewQueueActive = computed(
  () => isAdmin.value && forceDraft.value && isSingleQuestion.value,
);
const draftPreviewFallbackPath = computed(
  () => `/admin/${currentQuestionBank.value}/content`,
);

const approveQuestionDraftFromPreview = async () => {
  const adminApproveQuestion = httpsCallable(functions, "adminApproveQuestion");
  await adminApproveQuestion({
    draftId: currentQuestionId.value,
    collectionPath: collectionPath.value,
    changesFromOriginal: [],
  });
  return true;
};

const {
  isApprovalPending,
  approvalCompleted,
  canApproveClose,
  canApproveNext,
  completionRecovery,
  handleApproval: handleDraftPreviewApproval,
  advancePastStaleCurrent: advanceFromStaleDraftPreview,
  recoverFromCompletionError,
} = useAdminDraftPreviewApproval({
  kind: ADMIN_DRAFT_QUEUE_KIND_CONTENT,
  bankId: currentQuestionBank,
  currentId: currentQuestionId,
  route,
  router,
  canApprove: draftPreviewCanApprove,
  queueActive: draftPreviewQueueActive,
  isComplete: draftApprovalReady,
  approve: approveQuestionDraftFromPreview,
  fallbackAdminPath: draftPreviewFallbackPath,
  onSuccess: () => {
    alertStore.showAlert("Draft approved successfully", "success");
  },
  onApprovalError: (error) => {
    console.error("Error approving draft from session:", error);
    alertStore.showAlert("Failed to approve draft", "error");
  },
  onCompletionError: () => {
    alertStore.showAlert(
      "Draft was approved, but navigation or close failed. Return to drafts to continue.",
      "warning",
    );
  },
});

// Side nav
const { mobile } = useDisplay();
const questionDrawer = ref(false);
const questionDrawerSize = ref(mobile.value ? 300 : 500);

import { useReward } from "vue-rewards";
import QuestionInfoChips from "@/components/QuestionInfoChips.vue";
import TestCountDown from "@/components/TestCountDown.vue";

// Answering logic
const answerSaving = ref(false);
const finishRequested = ref(false);
const saveWrittenAnswer = httpsCallable(
  getFunctions(undefined, "europe-west2"),
  "saveWrittenSessionAnswer",
);
const handleAnswer = async (opt) => {
  if (
    answerSaving.value ||
    progressReadOnly.value ||
    sessionData.value?.status === "completed"
  )
    return;
  if (questionAnswered.value && !sessionData.value?.enableTestMode) return;
  const question = sessionData.value.questions[questionIndex.value];
  answerSaving.value = true;
  let saved = false;
  try {
    await saveWrittenAnswer({
      bank: currentQuestionBank.value,
      sessionId,
      questionId: question.questionId,
      optionId: opt.oID,
    });
    saved = true;
  } catch (error) {
    alertStore.showAlert(
      error.message || "Couldn't save your answer. Please try again.",
      "error",
    );
    console.error("Written answer save failed", {
      sessionId,
      questionId: question.questionId,
      error,
    });
  } finally {
    answerSaving.value = false;
    if (finishRequested.value) {
      finishRequested.value = false;
      if (saved) await endSession();
    }
  }
};

const questionAnswered = computed(() => {
  return (
    sessionData.value?.questions[questionIndex.value].chosenAnswerId !== null
  );
});

const answeredAll = computed(() => {
  if (!sessionData.value?.questions) return false;

  return sessionData.value?.questions.every((q) => q.chosenAnswerId !== null);
});

const showResults = computed(() => {
  const isTestCompleted =
    sessionData.value?.status === "completed" || progressReadOnly.value;
  const isQuestionAnswered =
    !sessionData.value?.enableTestMode && questionAnswered.value;

  if (isTestCompleted || isQuestionAnswered) {
    return true;
  }

  return false;
});

const showElements = ref(true);
const handleProgress = async (direction) => {
  if (answerSaving.value) return;
  showElements.value = false;
  if (direction === "next") {
    questionIndex.value++;
  } else if (direction === "back") {
    questionIndex.value--;
  }
  setTimeout(() => {
    showElements.value = true;
  }, 100);
};

// Get images
import { useFbStorageHelpers } from "@/composables/fbStorageHelpers";
const { allImgPaths, getUrls } = useFbStorageHelpers();

// Timer
const secondsRemaining = computed(() => {
  if (!sessionData.value?.enableTimer) return null;

  const endAt = sessionData.value?.timerEndAt;
  let endMs = null;

  if (endAt?.seconds) {
    endMs = endAt.seconds * 1000;
  } else if (typeof sessionData.value?.timerAmount === "number") {
    endMs = Date.now() + sessionData.value.timerAmount * 60 * 1000;
  }

  if (!endMs) return null;

  return Math.max(0, Math.floor(endMs - Date.now()));
});


const endSession = async () => {
  showUnansweredDialog.value = false;
  if (answerSaving.value) {
    finishRequested.value = true;
    return;
  }

  if (
    progressReadOnly.value ||
    sessionData.value?.status === "completed"
  )
    return;
  loading.value = true;
  try {
    const result = await statsStore.endSession({
      sessionId: sessionId,
      collectionPath: collectionPath.value,
    });
    if (result.success) {
      showOverview.value = true;
      // add confetti only the first time they finish the test
      const { reward: rewardRight } = useReward("confettiDiv", "confetti", {
        lifetime: 150,
        angle: 50,
        elementSize: 15,
        elementCount: 200,
        zIndex: 100,
        startVelocity: 40,
      });
      const { reward: rewardLeft } = useReward("confettiDiv", "confetti", {
        lifetime: 150,
        angle: 130,
        elementSize: 15,
        elementCount: 200,
        zIndex: 100,
        startVelocity: 40,
      });
      rewardRight();
      rewardLeft();
    } else {
      alertStore.showAlert(
        "An error occurred. Could not save results. Please check your internet connection and try again.",
        "error",
      );
      return;
    }
  } catch (error) {
    alertStore.showAlert(
      error.message || "Could not save results. Please try again.",
      "error",
    );
    console.error("Written session completion failed", {sessionId, bank: currentQuestionBank.value, error});
    return;
  } finally {
    loading.value = false;
  }
};

// Check for unanswered questions
const showUnansweredDialog = ref(false);
const checkUnanswered = () => {
  if (answeredAll.value) {
    endSession();
  } else {
    showUnansweredDialog.value = true;
  }
};

// qId,
// userEmail: currentUser.value.email,
// userId: currentUser.value.uid,
// sessionId: sessionId,

// Misc
const curvedDelay = (idx) => {
  return (1.5 / Math.sqrt(idx + 1)) * 400;
};

const perc = (num, total) => {
  if (total == 0 || isNaN(num) || isNaN(total)) {
    return 0;
  }
  return ((num / total) * 100).toFixed(1);
};

onMounted(async () => {
  window.addEventListener("keydown", handleKeydown);
  // hide chat
  window.$crisp.push(["do", "chat:hide"]);
  if (currentUser.value) {
    await checkAdminStatus();
  }
});

onUnmounted(() => {
  cleanupCommentsListener();
  window.removeEventListener("keydown", handleKeydown);
  // show it again
  window.$crisp.push(["do", "chat:show"]);
});

const handleKeydown = (event) => {
  // Check if the active element is a textarea
  if (event.target.tagName.toLowerCase() === "textarea") {
    return; // Exit the function, allowing default textarea behavior
  }
  const lastIndex = sessionData.value.questions.length - 1;
  if (event.key === "ArrowRight" && questionIndex.value < lastIndex) {
    handleProgress("next");
  } else if (event.key === "ArrowLeft" && questionIndex.value > 0) {
    handleProgress("back");
  }
};

// Add watchers for scrolling
watch(questionIndex, () => {
  window.scrollTo(0, 0);
});
watch(showOverview, (val) => {
  if (val) {
    window.scrollTo(0, 0);
  }
});

// progress so far, number of questions correct so far
const progressSoFar = computed(() => {
  if (!sessionData.value) return { num: 0, perc: 0 };
  const answeredQuestions = sessionData.value.questions.filter(
    (q) => q.chosenAnswerId !== null,
  );
  const correct = answeredQuestions.filter((q) => q.correct).length;
  return {
    num: correct,
    totalAnswered: answeredQuestions.length,
    perc: perc(correct, answeredQuestions.length),
  };
});

const fetchingComments = ref(false);
const comments = ref([]);
const replyTextareas = ref({});
const upvoteLoading = ref({});
const showCommentSettingsDialog = ref(false);
const commentSettingsSaving = ref(false);
let commentsUnsubscribe = null;

const {
  getCommentDisplayName,
  getCommentDisplayShortName,
  isCommentAuthorNameHidden,
} = useCommentPrivacy();
const currentUserHideName = ref(false);
const commentPrivacyLoaded = ref(true);
let commentPrivacyLoadPromise = null;
let commentPrivacyLoadUid = null;

const refreshCurrentUserHideName = async (uid = currentUser.value?.uid) => {
  if (!uid) {
    currentUserHideName.value = false;
    commentPrivacyLoaded.value = true;
    commentPrivacyLoadPromise = null;
    commentPrivacyLoadUid = null;
    return false;
  }

  if (commentPrivacyLoadPromise && commentPrivacyLoadUid === uid) {
    return commentPrivacyLoadPromise;
  }

  commentPrivacyLoaded.value = false;
  commentPrivacyLoadUid = uid;
  commentPrivacyLoadPromise = loadCurrentUserCommentHideName(db, uid)
    .then((hideName) => {
      if (currentUser.value?.uid === uid && hideName !== null) {
        currentUserHideName.value = hideName;
        commentPrivacyLoaded.value = true;
      }

      return hideName;
    })
    .finally(() => {
      if (commentPrivacyLoadUid === uid) {
        commentPrivacyLoadPromise = null;
      }
    });

  return commentPrivacyLoadPromise;
};

const ensureCurrentUserCommentPrivacyLoaded = async () => {
  const uid = currentUser.value?.uid;

  if (!uid) {
    alertStore.showAlert("Please sign in to add a comment.", "warning");
    return false;
  }

  if (!commentPrivacyLoaded.value) {
    await refreshCurrentUserHideName(uid);
  }

  if (!commentPrivacyLoaded.value) {
    alertStore.showAlert(
      "Failed to load comment privacy settings. Please try again.",
      "error",
    );
    return false;
  }

  return true;
};

watch(
  () => currentUser.value?.uid,
  (uid) => {
    if (!uid) {
      void refreshCurrentUserHideName(null);
      return;
    }

    void refreshCurrentUserHideName(uid);
  },
  { immediate: true },
);

const openCommentSettingsDialog = async () => {
  const uid = currentUser.value?.uid;
  if (!uid) {
    alertStore.showAlert("Please sign in to update comment settings.", "error");
    return;
  }

  await refreshCurrentUserHideName(uid);
  if (!commentPrivacyLoaded.value) {
    alertStore.showAlert(
      "Failed to load comment settings. Please try again.",
      "error",
    );
    return;
  }
  showCommentSettingsDialog.value = true;
};

const cleanupCommentsListener = () => {
  if (typeof commentsUnsubscribe === "function") {
    commentsUnsubscribe();
    commentsUnsubscribe = null;
  }
};

const resetCommentsState = () => {
  comments.value = [];
  newComment.value = "";
  replyLoading.value = {};
  editingCommentId.value = null;
  upvoteLoading.value = {};
  fetchingComments.value = false;
};

const saveCommentSettings = async (hideName) => {
  const uid = currentUser.value?.uid;

  if (!uid) {
    alertStore.showAlert("Please sign in to update comment settings.", "error");
    return;
  }

  if (Boolean(hideName) === currentUserHideName.value) {
    showCommentSettingsDialog.value = false;
    return;
  }

  commentSettingsSaving.value = true;

  try {
    await setDoc(
      doc(collection(db, "users"), uid),
      {
        commentSettings: {
          hideName: Boolean(hideName),
        },
      },
      { merge: true },
    );
    currentUserHideName.value = Boolean(hideName);
    showCommentSettingsDialog.value = false;
    alertStore.showAlert("Comment settings updated.", "success");
  } catch (error) {
    console.error("Error saving comment settings:", error);
    alertStore.showAlert(
      "Failed to save comment settings. Please try again.",
      "error",
    );
  } finally {
    commentSettingsSaving.value = false;
  }
};

const toggleReplyInput = (commentId) => {
  const comment = comments.value.find((c) => c.id === commentId);
  if (comment) {
    comment.showReplyInput = !comment.showReplyInput;
    if (comment.showReplyInput) {
      nextTick(() => {
        const textarea = replyTextareas.value[commentId];
        if (textarea) {
          textarea.focus();
        }
      });
    }
  }
};

// Add this function after your other helper functions
const updateNewCommentsTracker = async (commentId, isReply = false) => {
  try {
    const currentQuestionId =
      sessionData.value.questions[questionIndex.value]?.questionId;
    const newCommentsRef = collection(db, "new_comments_and_replies");
    await addDoc(newCommentsRef, {
      questionId: currentQuestionId,
      commentId: commentId,
      type: isReply ? "reply" : "comment",
      questionBank: collectionPath.value,
      draft: forceDraft.value,
      created: serverTimestamp(),
    });
  } catch (error) {
    console.error("Error updating new comments tracker:", error);
    // Don't throw error - this is a non-critical operation
  }
};

const addReply = async (parentCommentId) => {
  const parentComment = comments.value.find((c) => c.id === parentCommentId);
  if (
    parentComment &&
    parentComment.replyText?.trim() &&
    currentQuestionData.value &&
    !replyLoading.value[parentCommentId]
  ) {
    replyLoading.value[parentCommentId] = true;
    const currentQuestionId =
      sessionData.value.questions[questionIndex.value]?.questionId;
    try {
      const canSubmitReply = await ensureCurrentUserCommentPrivacyLoaded();
      if (!canSubmitReply) {
        return;
      }

      const commentsRef = collection(
        db,
        commentsCollectionPath.value,
        currentQuestionId,
        "comments",
      );
      const replyRef = await addDoc(commentsRef, {
        author: currentUserHideName.value
          ? "Anonymous learner"
          : userStore.basic.fName + " " + userStore.basic.lName,
        userId: currentUser.value.uid,
        text: parentComment.replyText,
        created: new Date(),
        likes: 0,
        byAdmin: isAdmin.value,
        hideNameFromOthers: currentUserHideName.value,
        parent: doc(
          db,
          commentsCollectionPath.value,
          currentQuestionId,
          "comments",
          parentCommentId,
        ),
      });

      // Track the new reply
      await updateNewCommentsTracker(replyRef.id, true);

      // notify the author of the parent comment unless they are replying to their own comment
      if (parentComment.userId !== currentUser.value.uid) {
        await dataStore.notifyUserParentComment({
          questionId: currentQuestionId,
          replyId: replyRef.id,
          userId: parentComment.userId, // to get their email in the backend
          parentFirstName: (parentComment.author || "").split(" ")[0],
          replyText: parentComment.replyText,
          parentText: parentComment.text,
          questionBank: questionBank.value,
          draft: forceDraft.value,
        });
      }

      parentComment.replyText = "";
      parentComment.showReplyInput = false;
    } catch (error) {
      console.error("Error adding reply:", error);
      alertStore.showAlert("Failed to add reply. Please try again.", "error");
    } finally {
      // Add a 1-second delay before setting replyLoading to false
      replyLoading.value[parentCommentId] = false;
    }
  }
};

const newComment = ref("");
const commentLoading = ref(false);
const replyLoading = ref({});
const editingCommentId = ref(null);
const commentsCollectionPath = computed(() => {
  if (forceDraft.value) {
    return `${collectionPath.value}drafts`;
  }

  return `${collectionPath.value}questions`;
});

const activeCommentsKey = computed(() => {
  if (!showResults.value || !currentQuestionId.value) {
    return null;
  }

  return `${commentsCollectionPath.value}/${currentQuestionId.value}`;
});

watch(
  activeCommentsKey,
  (newKey, oldKey) => {
    const questionId = currentQuestionId.value;

    cleanupCommentsListener();

    if (newKey !== oldKey) {
      resetCommentsState();
    }

    if (!newKey || !questionId) {
      return;
    }

    fetchingComments.value = true;

    const commentsRef = collection(
      db,
      commentsCollectionPath.value,
      questionId,
      "comments",
    );
    const q = query(commentsRef, orderBy("created", "desc"));

    commentsUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedComments = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          likes: doc.data().likes || 0,
          replies: [],
          userHasUpvoted:
            doc.data().upvotedBy?.includes(currentUser.value?.uid) || false,
          isCurrentUserAuthor: doc.data().userId === currentUser.value?.uid,
          isDeleted: doc.data().isDeleted || false,
          edited: doc.data().edited || false,
          editedAt: doc.data().editedAt,
        }));

        const topLevelComments = fetchedComments.filter(
          (comment) => !comment.parent,
        );
        const replies = fetchedComments.filter((comment) => comment.parent);

        topLevelComments.forEach((comment) => {
          comment.replies = replies.filter(
            (reply) => reply.parent?.id === comment.id,
          );
        });

        comments.value = topLevelComments.sort((a, b) => {
          return new Date(b.created) - new Date(a.created);
        });
        fetchingComments.value = false;
      },
      (error) => {
        console.error("Error fetching comments:", error);
        fetchingComments.value = false;
        alertStore.showAlert(
          "Failed to load comments. Please try again.",
          "error",
        );
      },
    );
  },
  { immediate: true },
);

const addComment = async () => {
  if (
    newComment.value.trim() &&
    currentQuestionData.value &&
    !commentLoading.value
  ) {
    commentLoading.value = true;
    try {
      const canSubmitComment = await ensureCurrentUserCommentPrivacyLoaded();
      if (!canSubmitComment) {
        return;
      }

      const currentQuestionId =
        sessionData.value.questions[questionIndex.value]?.questionId;
      const commentsRef = collection(
        db,
        commentsCollectionPath.value,
        currentQuestionId,
        "comments",
      );
      const commentRef = await addDoc(commentsRef, {
        author: currentUserHideName.value
          ? "Anonymous learner"
          : userStore.basic.fName + " " + userStore.basic.lName,
        userId: currentUser.value.uid,
        text: newComment.value,
        created: new Date(),
        likes: 0,
        byAdmin: isAdmin.value,
        hideNameFromOthers: currentUserHideName.value,
      });
      // now we will add it to the /new_comments_and_replies collection doc
      // so we can track comments once a day
      // Track the new comment
      await updateNewCommentsTracker(commentRef.id, false);

      // Notify admins immediately
      const notifyAdmins = httpsCallable(functions, "notifyAdminsOnNewComment");

      notifyAdmins({
        questionId: currentQuestionId,
        bank: questionBank.value,
        commentText: newComment.value,
        author: userStore.basic.fName + " " + userStore.basic.lName,
        authorId: currentUser.value.uid,
        isDraft: forceDraft.value,
      });

      newComment.value = "";
    } catch (error) {
      console.error("Error adding comment:", error);
      alertStore.showAlert("Failed to add comment. Please try again.", "error");
    } finally {
      commentLoading.value = false;
    }
  }
};

const deleteComment = async (commentId, isReply = false) => {
  if (!currentQuestionData.value) return;

  const currentQuestionId =
    sessionData.value.questions[questionIndex.value]?.questionId;
  const commentRef = doc(
    db,
    commentsCollectionPath.value,
    currentQuestionId,
    "comments",
    commentId,
  );

  try {
    const commentDoc = await getDoc(commentRef);
    const commentData = commentDoc.data();

    if (!commentData) {
      console.error("Comment not found");
      return;
    }

    const comment = comments.value.find((c) => c.id === commentId);
    const hasReplies = comment && comment.replies && comment.replies.length > 0;

    if (!isReply && hasReplies) {
      // If it's a top-level comment with replies, mark it as deleted
      await updateDoc(commentRef, {
        text: "[deleted]",
        author: "[deleted]",
        isDeleted: true,
      });
    } else {
      // If it's a reply or a top-level comment without replies, delete it
      await deleteDoc(commentRef);
    }

    alertStore.showAlert("Comment deleted successfully.", "success");
  } catch (error) {
    console.error("Error deleting comment:", error);
    alertStore.showAlert(
      "Failed to delete comment. Please try again.",
      "error",
    );
  }
};

const toggleEditMode = (commentId) => {
  if (editingCommentId.value === commentId) {
    editingCommentId.value = null;
  } else {
    editingCommentId.value = commentId;
  }
};

// Add this new function to handle comment editing
const editComment = async (commentId, newText) => {
  if (!currentQuestionData.value || !newText.trim()) return;

  const currentQuestionId =
    sessionData.value.questions[questionIndex.value]?.questionId;
  const commentRef = doc(
    db,
    commentsCollectionPath.value,
    currentQuestionId,
    "comments",
    commentId,
  );

  try {
    await updateDoc(commentRef, {
      text: newText.trim(),
      edited: true,
      editedAt: new Date(),
    });
    editingCommentId.value = null;
    alertStore.showAlert("Comment updated successfully.", "success");
  } catch (error) {
    console.error("Error editing comment:", error);
    alertStore.showAlert("Failed to edit comment. Please try again.", "error");
  }
};

const commentOrder = ref("latest");

const sortedComments = computed(() => {
  return [...comments.value].sort((a, b) => {
    if (commentOrder.value === "latest") {
      return new Date(b.created) - new Date(a.created);
    } else {
      return b.likes - a.likes;
    }
  });
});

const sortedReplies = (replies) => {
  return orderRepliesChronologically(replies);
};

const avatarSize = 40;

const getInitials = (name) => {
  if (!name) return "";
  if (name === "[deleted]") return "D";
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

// Convert various timestamp shapes (Date, Firestore Timestamp, plain object, string) to Date
const toDateSafe = (timestamp) => {
  if (!timestamp) return null;

  if (timestamp instanceof Date) return timestamp;

  if (typeof timestamp?.toDate === "function") {
    return timestamp.toDate();
  }

  if (typeof timestamp?.seconds === "number") {
    return new Date(timestamp.seconds * 1000);
  }

  const parsed = new Date(timestamp);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatAbsoluteDateTime = (timestamp) => {
  const date = toDateSafe(timestamp);
  if (!date) return "";

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return "";

  const date = toDateSafe(timestamp);
  if (!date) return "";

  const now = new Date();
  const diffSeconds = Math.floor((now - date) / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return `${diffYears}y ago`;
};

const toggleUpvote = async (commentId) => {
  if (!currentQuestionData.value || upvoteLoading.value[commentId]) return;

  upvoteLoading.value[commentId] = true;

  try {
    const currentQuestionId =
      sessionData.value.questions[questionIndex.value]?.questionId;
    const commentRef = doc(
      db,
      commentsCollectionPath.value,
      currentQuestionId,
      "comments",
      commentId,
    );
    const commentDoc = await getDoc(commentRef);
    const commentData = commentDoc.data();

    const hasUpvoted = commentData.upvotedBy?.includes(currentUser.value.uid);

    await updateDoc(commentRef, {
      likes: increment(hasUpvoted ? -1 : 1),
      upvotedBy: hasUpvoted
        ? arrayRemove(currentUser.value.uid)
        : arrayUnion(currentUser.value.uid),
    });
  } catch (error) {
    console.error("Error toggling upvote:", error);
    alertStore.showAlert("Failed to update. Please try again.", "error");
  } finally {
    upvoteLoading.value[commentId] = false;
  }
};

const toggleResolved = async (commentId, replyId = null) => {
  if (!currentQuestionData.value) return;

  try {
    const currentQuestionId =
      sessionData.value.questions[questionIndex.value]?.questionId;
    const commentRef = doc(
      db,
      commentsCollectionPath.value,
      currentQuestionId,
      "comments",
      replyId ? replyId : commentId,
    );

    let currentResolved = null;
    if (replyId) {
      const comment = comments.value.find((c) => c.id === commentId);
      const reply = comment.replies.find((r) => r.id === replyId);
      currentResolved = !!reply?.resolved;
    } else {
      const comment = comments.value.find((c) => c.id === commentId);
      currentResolved = !!comment?.resolved;
    }

    await updateDoc(commentRef, {
      resolved: !currentResolved,
    });
  } catch (error) {
    console.error("Error toggling resolved status:", error);
    alertStore.showAlert("Failed to update. Please try again.", "error");
  }
};

// Remove the hardcoded adminIds array and update the imports
import { getFunctions, httpsCallable } from "firebase/functions";
import { formatRFC7231 } from "date-fns";
import { isDraftAlreadyExistsError } from "@/utils/draftErrors.js";
import { getAdminContentEditUrl } from "@/utils/videoStationAdminRoutes.mjs";
const functions = getFunctions();
functions.region = "europe-west2";

const {
  commentAuthorContactOpen,
  commentAuthorContactLoading,
  commentAuthorContactError,
  commentAuthorContact,
  canOpenCommentAuthorContact,
  openCommentAuthorContact,
} = useCommentAuthorContact({
  canModerate: computed(() => adminAccessStore.canModerate),
  resolveContext: () => ({
    sourceCollectionPath: commentsCollectionPath.value,
    itemId: currentQuestionId.value,
    bankId: questionBank.value,
  }),
});

// Add this method to check current user's admin status
const checkAdminStatus = async () => {
  try {
    await adminAccessStore.refresh(currentUser.value || undefined);
  } catch (error) {
    console.error("Error checking admin status:", error);
    adminAccessStore.clear();
  }
};

const editQuestionButtonLoading = ref(false);
const adminEditQuestion = async () => {
  editQuestionButtonLoading.value = true;
  if (!isAdmin.value) {
    editQuestionButtonLoading.value = false;
    return;
  }

  const questionId = route.params.id;
  const editUrl = getAdminContentEditUrl(questionBank.value, questionId);
  try {
    if (draftExists.value || forceDraft.value) {
      window.open(editUrl, "_blank");
    } else {
      const adminCreateNewDraft = httpsCallable(
        functions,
        "adminCreateNewDraft",
      );
      const result = await adminCreateNewDraft({
        questionId: questionId,
        collectionPath: collectionPath.value,
      });
      const newDraftId = result.data.id;
      window.open(
        getAdminContentEditUrl(questionBank.value, newDraftId),
        "_blank",
      );
    }
  } catch (error) {
    console.error("Error opening question editor:", error);
    // The draft shares its doc id with the question, so if one already
    // exists just open it instead of failing.
    if (isDraftAlreadyExistsError(error)) {
      window.open(editUrl, "_blank");
    } else {
      alertStore.showAlert(
        error.message || "Failed to open question editor",
        "error",
      );
    }
  } finally {
    editQuestionButtonLoading.value = false;
  }
};
</script>

<style lang="scss">
.noselect {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.answer-div {
  border: 1px solid #fff;
}
.answer-div:hover {
  cursor: pointer;
  border: 1px solid #333;
}

.comment-author-profile-avatar,
.comment-author-profile-name {
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
}

.comment-author-profile-avatar {
  display: inline-flex;
  flex-shrink: 0;
  padding: 0;
  border-radius: 50%;
}

.comment-author-profile-name {
  color: rgb(var(--v-theme-primary));
  padding: 0;
  font-weight: inherit;
  text-align: left;
}

.comment-author-profile-name:hover {
  text-decoration: underline;
}

.comment-author-profile-avatar:focus-visible,
.comment-author-profile-name:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.answers-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.answers-grid--images {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.answer-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
}

.answer-image-wrapper {
  border-radius: 8px;
  overflow: hidden;
}

@media (max-width: 960px) {
  .answers-grid--images {
    grid-template-columns: 1fr;
  }
}

.action-pane {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 75px;
  z-index: 100;
}

#confettiDiv {
  position: fixed;
  top: 30vh;
  left: 50vw;
  width: 2px;
  height: 2px;
  z-index: 100;
}

.thick-outline {
  border: 5px solid #0077b6 !important;
}

.comment-order-select :deep(.v-field__input) {
  padding-top: 5px;
  padding-bottom: 5px;
}
// Update the general table styles to be dark mode compatible
:deep(table) {
  border-collapse: collapse !important;
  margin: 1rem 0 !important;
  width: 100% !important;
  min-width: 75px !important;
  table-layout: fixed !important; // Added
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
}

:deep(th),
:deep(td) {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  padding: 8px !important;
  text-align: left !important;
  min-width: 25px !important;
  word-wrap: break-word !important; // Added
  overflow-wrap: break-word !important; // Added
}

:deep(th) {
  background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
}

:deep(th p),
:deep(td p) {
  margin: 0 !important;
  padding: 0 !important;
  display: block !important;
}

:deep(colgroup),
:deep(col) {
  width: auto !important;
}

/* Fix bullet point styling */
:deep(li) {
  margin: 0.5em 0 !important; /* Add vertical spacing between list items */
  padding-left: 0.5em !important; /* Add left padding */
  line-height: 1.4 !important; /* Improve line height for readability */
}

:deep(ul),
:deep(ol) {
  margin: 0.5em 0 !important; /* Add spacing around lists */
  padding-left: 2em !important; /* Proper indentation for bullet points */
  list-style-position: outside !important; /* Keep bullets outside the text block */
}

// Add paragraph spacing styles
:deep(p) {
  margin: 1em 0 !important;
  line-height: 1.5 !important;
}

// Add empty paragraph styles
:deep(p:empty) {
  height: 1em !important;
  margin: 1em 0 !important;
}

// Add center-aligned paragraph styles
:deep(p[style*="text-align: center"]) {
  margin: 1em 0 !important;

  &:empty {
    margin: 1em 0 !important;
  }
}

// Add spacing between elements
:deep(> * + *) {
  margin-top: 0.75em !important;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  :deep(table) {
    display: block !important;
    overflow-x: auto !important;
  }
}

.tiptap {
  :first-child {
    margin-top: 0;
  }

  padding: 1rem;
  min-height: 300px;

  /* Link styles */
  a {
    color: #2196f3;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #1976d2;
    }
  }

  /* YouTube embed */
  div[data-youtube-video] {
    margin: 1rem 0;
    display: inline-block;
    vertical-align: top;
    margin-right: 1rem; // Add spacing between videos

    &:last-child {
      margin-right: 0;
    }

    iframe {
      border-radius: 4px;
      display: block;
      max-width: 100%;
    }

    &.ProseMirror-selectednode iframe {
      outline: 2px solid #2196f3;
    }
  }

  /* Add container for multiple videos */
  p:has(div[data-youtube-video]) {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: flex-start;
  }

  /* Responsive adjustments for videos */
  @media (max-width: 768px) {
    div[data-youtube-video] {
      width: 100%;
      margin-right: 0;

      iframe {
        width: 100%;
        height: auto;
        aspect-ratio: 16/9;
      }
    }
  }

  /* Blockquote styles */

  blockquote {
    margin: 1em 0;
    padding: 0.5em 1em;
    border-left: 4px solid rgb(var(--v-theme-primary));
    background-color: rgba(var(--v-theme-primary), 0.05);
    font-style: italic;

    // Remove margin from first/last paragraph inside blockquote
    p:first-child {
      margin-top: 0;
    }
    p:last-child {
      margin-bottom: 0;
    }
  }

  /* Horizontal rule styles */
  hr {
    border: none;
    border-top: 2px solid rgba(13, 13, 13, 0.1);
    margin: 2rem 0;
    padding: 0;
    width: 100%;
    height: 0;
    display: block;
    clear: both;
  }

  /* List styles */
  ul,
  ol {
    padding: 0 1rem;
    margin: 1.25rem 1rem 1.25rem 0.4rem;

    li p {
      margin-top: 0.25em;
      margin-bottom: 0.25em;
    }
  }

  /* Heading styles */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    line-height: 1.1;
    margin-top: 2.5rem;
    text-wrap: pretty;
  }

  h1,
  h2 {
    margin-top: 3.5rem;
    margin-bottom: 1.5rem;
  }

  h1 {
    font-size: 1.4rem;
  }

  h2 {
    font-size: 1.2rem;
  }

  h3 {
    font-size: 1.1rem;
  }

  h4,
  h5,
  h6 {
    font-size: 1rem;
  }

  /* Code and preformatted text styles */
  code {
    background-color: var(--purple-light);
    border-radius: 0.4rem;
    color: var(--black);
    font-size: 0.85rem;
    padding: 0.25em 0.3em;
  }

  pre {
    background: var(--black);
    border-radius: 0.5rem;
    color: var(--white);
    font-family: "JetBrainsMono", monospace;
    margin: 1.5rem 0;
    padding: 0.75rem 1rem;

    code {
      background: none;
      color: inherit;
      font-size: 0.8rem;
      padding: 0;
    }
  }

  &:focus {
    outline: none;
  }

  p {
    margin: 1em 0;
    line-height: 1.5;
  }

  // Add some spacing between elements
  > * + * {
    margin-top: 0.75em;
  }

  /* Add placeholder styling */
  &.is-empty::before {
    content: "Start typing...";
    color: #adb5bd;
    float: left;
    pointer-events: none;
    height: 0;
  }

  /* Improve focus state */
  &:focus {
    outline: none;
    background-color: rgba(#000, 0.01);
  }

  // Base table styles
  table {
    border-collapse: collapse;
    margin: 1rem 0;
    width: 100%;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);

    td,
    th {
      border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
      padding: 0.5rem;
      position: relative;
    }

    th {
      background-color: rgba(var(--v-theme-on-surface), 0.04);
      font-weight: bold;
    }

    .selectedCell {
      position: relative;

      &::after {
        background: rgba(33, 150, 243, 0.1);
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        pointer-events: none;
      }
    }

    .column-resize-handle {
      background-color: #2196f3;
      bottom: -2px;
      position: absolute;
      right: -2px;
      pointer-events: none;
      top: 0;
      width: 4px;
    }
  }

  &.resize-cursor {
    cursor: col-resize;
  }
}

// Mobile-specific styles
@media (max-width: 600px) {
  .content-width {
    width: 100%;
    padding: 0 8px;

    :deep(table) {
      font-size: 14px;
      display: block;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      white-space: nowrap; // Keep table content on one line on mobile

      // Table wrapper to maintain structure
      > tbody,
      > thead {
        display: table;
        width: 100%;
      }

      td,
      th {
        min-width: 100px; // Only set min-width on mobile
        white-space: normal; // Allow text wrapping within cells
      }
    }
  }
}

.v-html-content {
  :deep(p) {
    margin: 1em 0 !important;
    line-height: 1.5 !important;
    display: block !important;
    min-height: 1em !important; // Ensures empty paragraphs have height
  }

  :deep(p:empty) {
    height: 1em !important;
    margin: 1em 0 !important;
    display: block !important;
  }

  :deep(span) {
    display: inline !important;
  }
}

/* Answer title styling */
.answer-title {
  font-weight: bold;
  color: var(--v-theme-primary);
  line-height: 1.2;
  font-size: 1.75rem;
}

/* Responsive styling for answer title */
@media (max-width: 600px) {
  .answer-title {
    font-size: 1.5rem;
  }
}

/* Reset to normal HTML spacing like your second page */
.v-html-content.tiptap {
  /* Reset heading margins to normal values */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 1.5em !important;
    margin-bottom: 0.5em !important;
    line-height: 1.2 !important;
  }

  /* First heading has no top margin */
  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child,
  h5:first-child,
  h6:first-child {
    margin-top: 0 !important;
  }

  /* Normal paragraph spacing */
  p {
    margin: 1em 0 !important;
    line-height: 1.5 !important;
  }

  /* Normal element spacing */
  > * + * {
    margin-top: 0.75em !important;
  }
}

@media (min-width: 601px) {
  :deep(.session-ai-chat .chat-fab) {
    bottom: 10px !important;
  }
}

@media (max-width: 600px) {
  :deep(.session-ai-chat .chat-fab) {
    right: auto !important;
    left: 16px !important;
  }
}
</style>
